/**
 * Elite Batch 2, Part 1 — Workflow Templates 48-72
 *
 * Production-grade prebuilt workflows: AI content creation, code review,
 * compliance, customer journey, scheduling, and knowledge management.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 2, PART 1 — WORKFLOWS 48-72
// =============================================================================

export const ELITE_BATCH2_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 48. AI BLOG OUTLINE GENERATOR (8 nodes)
  // ===================================================================

  "AI Blog Outline Generator": {
    nodes: [
      trigger("Webhook: Blog brief submitted", "webhook_app", {
        source: "Content Calendar / CMS / Manual",
        event: "brief.submitted",
        howItStarts:
          "A content brief is submitted with a topic, target keywords, and audience description",
        setup:
          "In your content calendar tool, configure a webhook to POST brief data to this workflow URL. Or submit briefs manually via API with topic, keywords, and audience fields.",
        inputFields: [],
      }),

      // 2. Extract brief details
      aiNode(
        "2",
        "AI extract brief details",
        280,
        "Read this blog brief payload and extract the details regardless of format. Find the topic, target keywords, target audience, desired word count, and content goal. Respond JSON: {topic: string, keywords: string, audience: string, word_count: string, content_goal: string, brand_name: string}",
        "{webhook_payload}",
        [
          { field: "topic", as: "topic", type: "string" },
          { field: "keywords", as: "keywords", type: "string" },
          { field: "audience", as: "audience", type: "string" },
          { field: "word_count", as: "word_count", type: "string" },
          { field: "content_goal", as: "content_goal", type: "string" },
          { field: "brand_name", as: "brand_name", type: "string" },
        ],
      ),

      // 3. SerpAPI competitor research
      n("3", "hitApi", "SerpAPI: Research top-ranking pages", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={keywords}&num=10&api_key={serpapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results",
              type: "string",
            },
            {
              name: "organic_results[0].title",
              customName: "top_title",
              type: "string",
            },
            {
              name: "organic_results[0].snippet",
              customName: "top_snippet",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI extract competitor headings and generate outline
      aiNode(
        "4",
        "AI extract competitor headings & build outline",
        760,
        "You are an expert content strategist. Analyze these SERP results and extract the common heading patterns from top-ranking pages. Then generate a comprehensive blog outline that covers gaps competitors miss. Include H2s and H3s, suggested word counts per section, and internal linking opportunities. Respond JSON: {competitor_headings: string, outline_sections: string, h2_count: number, suggested_total_words: number, gap_topics: string, internal_links: string}",
        "Topic: {topic}, Keywords: {keywords}, Audience: {audience}, Target words: {word_count}, Goal: {content_goal}, SERP results: {serp_results}",
        [
          {
            field: "competitor_headings",
            as: "competitor_headings",
            type: "string",
          },
          { field: "outline_sections", as: "outline_sections", type: "string" },
          { field: "h2_count", as: "h2_count", type: "number" },
          {
            field: "suggested_total_words",
            as: "suggested_total_words",
            type: "number",
          },
          { field: "gap_topics", as: "gap_topics", type: "string" },
          { field: "internal_links", as: "internal_links", type: "string" },
        ],
      ),

      // 5. AI brand voice pass
      aiNode(
        "5",
        "AI brand voice alignment pass",
        1000,
        "You are a brand voice editor. Take this blog outline and refine the section titles and descriptions to match the brand voice. Ensure headings are engaging, on-brand, and optimized for the target audience. Score the overall quality 0-100. Respond JSON: {refined_outline: string, voice_score: number, tone_notes: string, headline_suggestions: string}",
        "Brand: {brand_name}, Audience: {audience}, Outline: {outline_sections}, Gap topics: {gap_topics}",
        [
          { field: "refined_outline", as: "refined_outline", type: "string" },
          { field: "voice_score", as: "voice_score", type: "number" },
          { field: "tone_notes", as: "tone_notes", type: "string" },
          {
            field: "headline_suggestions",
            as: "headline_suggestions",
            type: "string",
          },
        ],
      ),

      // 6. Quality scoring condition
      n("6", "condition", "Voice score >= 70?", 1240, {
        conditionData: [
          {
            variable: "voice_score",
            value: "70",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Publish outline to Google Docs
      n("7", "hitApi", "Create Google Docs outline draft", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Blog Outline: {topic}" },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"{refined_outline}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "doc_id", type: "string" },
            { name: "title", customName: "doc_title", type: "string" },
          ],
        },
      }),

      // 8. Notify editorial team on Slack
      slackNode(
        "8",
        "Notify editorial: outline ready",
        1720,
        "Blog outline ready for *{topic}*\\nVoice score: {voice_score}/100\\nSections: {h2_count} H2s\\nDoc: https://docs.google.com/document/d/{doc_id}\\nHeadline ideas: {headline_suggestions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // pass quality -> publish to Docs
      e("6", "5", "output_2"), // fail quality -> re-run brand voice pass
      e("7", "8"),
    ],
    summary: `**AI Blog Outline Generator** — Turns a content brief into a research-backed, brand-aligned blog outline.\n\n1. Content brief webhook fires with topic, keywords, and audience\n2. **AI extracts** brief details from payload\n3. **SerpAPI** researches top-ranking pages for the target keywords\n4. **AI analyzes** competitor headings and generates a gap-filling outline\n5. **AI brand voice pass** refines section titles and scores quality\n6. Quality gate ensures voice score meets threshold\n7. **Google Docs** draft created with the refined outline\n8. **Slack notification** alerts editorial team with doc link\n\n**To activate:** Say "connect openai", "connect serpapi", "connect google docs", "connect slack"`,
  },

  // ===================================================================
  // 49. AI BRAINSTORMING PARTNER (7 nodes)
  // ===================================================================

  "AI Brainstorming Partner": {
    nodes: [
      trigger("Webhook: Brainstorm challenge submitted", "webhook_app", {
        source: "Slack / Internal Tool / Manual",
        event: "challenge.submitted",
        howItStarts:
          "A team member submits a creative challenge or problem statement for brainstorming",
        setup:
          "Configure a Slack slash command or internal form to POST challenge data to this workflow URL. Include the problem statement, constraints, and desired outcome.",
        inputFields: [],
      }),

      // 2. AI generate diverse ideas from 4 frames
      aiNode(
        "2",
        "AI brainstorm from 4 creative frames",
        280,
        "You are a world-class brainstorming facilitator. Generate diverse ideas for this challenge using 4 distinct thinking frames: (1) First Principles — strip to fundamentals and rebuild, (2) Analogy Transfer — borrow solutions from unrelated domains, (3) Constraint Inversion — what if we removed or reversed a key constraint, (4) Future Backwards — imagine the ideal outcome and work backward. Generate 3 ideas per frame (12 total). For each idea provide a title, description, and feasibility score 1-10. Respond JSON: {challenge: string, ideas: string, total_ideas: number, avg_feasibility: number, top_idea_title: string, top_idea_score: number}",
        "{webhook_payload}",
        [
          { field: "challenge", as: "challenge", type: "string" },
          { field: "ideas", as: "ideas", type: "string" },
          { field: "total_ideas", as: "total_ideas", type: "number" },
          { field: "avg_feasibility", as: "avg_feasibility", type: "number" },
          { field: "top_idea_title", as: "top_idea_title", type: "string" },
          { field: "top_idea_score", as: "top_idea_score", type: "number" },
        ],
      ),

      // 3. Feasibility threshold condition
      n("3", "condition", "Average feasibility >= 5?", 520, {
        conditionData: [
          {
            variable: "avg_feasibility",
            value: "5",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI enrich top ideas with examples
      aiNode(
        "4",
        "AI enrich ideas with real examples",
        760,
        "You are a strategy consultant. Take the top-scoring brainstorm ideas and enrich each one with: a real-world example of a similar approach, estimated effort (days), potential impact (1-10), and suggested first step. Respond JSON: {enriched_ideas: string, top_3_summary: string, fastest_win: string, highest_impact: string}",
        "Challenge: {challenge}, Ideas: {ideas}",
        [
          { field: "enriched_ideas", as: "enriched_ideas", type: "string" },
          { field: "top_3_summary", as: "top_3_summary", type: "string" },
          { field: "fastest_win", as: "fastest_win", type: "string" },
          { field: "highest_impact", as: "highest_impact", type: "string" },
        ],
      ),

      // 5. Create action cards
      aiNode(
        "5",
        "AI create action cards",
        1000,
        "You are a project planner. Convert the enriched brainstorm ideas into structured action cards. Each card should have: title, owner_suggestion, priority (P0-P3), estimated_hours, dependencies, and definition_of_done. Respond JSON: {action_cards: string, total_cards: number, p0_count: number, total_estimated_hours: number}",
        "Challenge: {challenge}, Enriched ideas: {enriched_ideas}, Fastest win: {fastest_win}",
        [
          { field: "action_cards", as: "action_cards", type: "string" },
          { field: "total_cards", as: "total_cards", type: "number" },
          { field: "p0_count", as: "p0_count", type: "number" },
          {
            field: "total_estimated_hours",
            as: "total_estimated_hours",
            type: "number",
          },
        ],
      ),

      // 6. Save to Notion
      n("6", "hitApi", "Save brainstorm to Notion database", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{notion_db_id}"}' },
            {
              key: "properties",
              value:
                '{"Challenge":{"title":[{"text":{"content":"{challenge}"}}]},"Top Idea":{"rich_text":[{"text":{"content":"{top_idea_title}"}}]},"Ideas Count":{"number":{total_ideas}},"Status":{"select":{"name":"Ready for Review"}}}',
            },
            {
              key: "children",
              value:
                '[{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{action_cards}"}}]}}]',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),

      // 7. Notify requester
      slackNode(
        "7",
        "Notify team: brainstorm results ready",
        1480,
        "Brainstorm complete for: *{challenge}*\\n{total_ideas} ideas generated, top idea: {top_idea_title} (score: {top_idea_score}/10)\\n{total_cards} action cards created ({p0_count} P0s)\\nFastest win: {fastest_win}\\nNotion: https://notion.so/{notion_page_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // feasible -> enrich
      e("3", "2", "output_2"), // low feasibility -> regenerate with different approach
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Brainstorming Partner** — Generates diverse ideas from 4 creative thinking frames and converts them into action cards.\n\n1. Challenge submitted via webhook or Slack command\n2. **AI generates 12 ideas** using First Principles, Analogy Transfer, Constraint Inversion, and Future Backwards\n3. Feasibility threshold filters low-quality batches\n4. **AI enriches** top ideas with real-world examples and effort estimates\n5. **AI creates** structured action cards with priorities and timelines\n6. Results saved to **Notion** database\n7. **Slack notification** with top ideas and action card summary\n\n**To activate:** Say "connect openai", "connect notion", "connect slack"`,
  },

  // ===================================================================
  // 50. AI BRAND VOICE GUARDIAN (8 nodes)
  // ===================================================================

  "AI Brand Voice Guardian": {
    nodes: [
      trigger("Webhook: Content submitted for voice check", "webhook_app", {
        source: "CMS / Google Docs / Content Pipeline",
        event: "content.submitted",
        howItStarts:
          "A piece of content is submitted for brand voice review before publication",
        setup:
          "Configure your CMS or content pipeline to POST content to this workflow URL when a draft moves to the review stage. Include the full text and author email.",
        inputFields: [],
      }),

      // 2. Extract content and retrieve brand rules
      aiNode(
        "2",
        "AI extract content details",
        280,
        "Read this content submission payload and extract the details regardless of format. Find the content text, author email, content type (blog/email/social/ad), channel, and any metadata. Respond JSON: {content_text: string, author_email: string, content_type: string, channel: string, title: string, word_count: number}",
        "{webhook_payload}",
        [
          { field: "content_text", as: "content_text", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "channel", as: "channel", type: "string" },
          { field: "title", as: "title", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 3. Retrieve brand rules from database
      n("3", "hitApi", "Fetch brand voice rules from config API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-company.com/brand-voice/rules?content_type={content_type}&channel={channel}",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "rules.tone", customName: "tone_rules", type: "string" },
            {
              name: "rules.vocabulary",
              customName: "vocab_rules",
              type: "string",
            },
            {
              name: "rules.banned_words",
              customName: "banned_words",
              type: "string",
            },
            {
              name: "rules.style_guide",
              customName: "style_guide",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI tone and vocabulary analysis
      aiNode(
        "4",
        "AI analyze tone, vocab & compliance",
        760,
        "You are a brand voice auditor. Analyze this content against the brand voice rules. Check tone alignment, vocabulary compliance, banned word usage, readability level, and style guide adherence. Generate a detailed scorecard. Score overall compliance 0-100. Respond JSON: {overall_score: number, tone_score: number, vocab_score: number, banned_word_violations: string, readability_grade: string, specific_issues: string, section_scores: string}",
        "Content: {content_text}, Type: {content_type}, Channel: {channel}, Tone rules: {tone_rules}, Vocab rules: {vocab_rules}, Banned words: {banned_words}, Style guide: {style_guide}",
        [
          { field: "overall_score", as: "overall_score", type: "number" },
          { field: "tone_score", as: "tone_score", type: "number" },
          { field: "vocab_score", as: "vocab_score", type: "number" },
          {
            field: "banned_word_violations",
            as: "banned_word_violations",
            type: "string",
          },
          {
            field: "readability_grade",
            as: "readability_grade",
            type: "string",
          },
          { field: "specific_issues", as: "specific_issues", type: "string" },
          { field: "section_scores", as: "section_scores", type: "string" },
        ],
      ),

      // 5. Quality condition
      n("5", "condition", "Overall voice score >= 80?", 1000, {
        conditionData: [
          {
            variable: "overall_score",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI rewrite failing sections
      aiNode(
        "6",
        "AI rewrite off-brand sections",
        1240,
        "You are a brand voice editor. Rewrite the sections that scored below threshold while preserving the original meaning and factual content. Apply the brand tone rules, fix vocabulary violations, and remove banned words. Respond JSON: {rewritten_content: string, changes_made: string, change_count: number, new_score_estimate: number}",
        "Original: {content_text}, Issues: {specific_issues}, Section scores: {section_scores}, Tone rules: {tone_rules}, Vocab rules: {vocab_rules}, Banned words: {banned_words}",
        [
          {
            field: "rewritten_content",
            as: "rewritten_content",
            type: "string",
          },
          { field: "changes_made", as: "changes_made", type: "string" },
          { field: "change_count", as: "change_count", type: "number" },
          {
            field: "new_score_estimate",
            as: "new_score_estimate",
            type: "number",
          },
        ],
        460,
      ),

      // 7. Notify author
      emailNode(
        "7",
        "Email author: voice review results",
        1480,
        "{author_email}",
        "Brand Voice Review: {title} — Score {overall_score}/100",
        'Hi,\\n\\nYour content \\"{title}\\" has been reviewed for brand voice compliance.\\n\\nOverall Score: {overall_score}/100\\nTone: {tone_score}/100 | Vocabulary: {vocab_score}/100\\nReadability: {readability_grade}\\n\\nIssues found:\\n{specific_issues}\\n\\nBanned word violations:\\n{banned_word_violations}\\n\\nChanges suggested: {change_count}\\n\\nPlease review and update before publication.',
      ),

      // 8. Log trend data
      n("8", "hitApi", "Log voice trend to analytics API", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/brand-voice/trends",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "author", value: "{author_email}" },
            { key: "content_type", value: "{content_type}" },
            { key: "overall_score", value: "{overall_score}" },
            { key: "tone_score", value: "{tone_score}" },
            { key: "vocab_score", value: "{vocab_score}" },
            { key: "word_count", value: "{word_count}" },
            { key: "timestamp", value: "{timestamp}" },
          ],
          variables: [
            { name: "id", customName: "trend_log_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // pass -> notify author (approved)
      e("5", "6", "output_2"), // fail -> AI rewrite
      e("6", "7"), // rewrite -> notify author with suggestions
      e("7", "8"),
    ],
    summary: `**AI Brand Voice Guardian** — Automatically audits content against brand rules and rewrites off-brand sections.\n\n1. Content submitted via CMS webhook for voice review\n2. **AI extracts** content text, author, type, and channel\n3. **Brand rules API** fetches tone, vocabulary, and style guide rules\n4. **AI audits** tone, vocabulary, banned words, readability, and style compliance\n5. Quality gate routes based on overall score\n6. Failing content gets **AI section rewrites** preserving meaning\n7. **Email notification** to author with scorecard and suggestions\n8. Trend data logged for **analytics** on voice compliance over time\n\n**To activate:** Say "connect openai", "connect your brand rules API", "connect email"`,
  },

  // ===================================================================
  // 51. AI CALENDAR OPTIMIZER (8 nodes)
  // ===================================================================

  "AI Calendar Optimizer": {
    nodes: [
      trigger("Schedule: Weekly calendar optimization", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs weekly to analyze your calendar, identify optimization opportunities, and suggest rescheduling",
        setup:
          "Connect your Google Calendar. The workflow will pull upcoming events, analyze meeting patterns, and suggest optimizations to protect focus time.",
        inputFields: [],
      }),

      // 2. Pull calendar snapshot
      n("2", "hitApi", "Pull Google Calendar events (next 7 days)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={week_start_iso}&timeMax={week_end_iso}&singleEvents=true&orderBy=startTime&maxResults=100",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "calendar_events", type: "string" },
            { name: "summary", customName: "calendar_name", type: "string" },
          ],
        },
      }),

      // 3. Enrich with tasks and deadlines
      n("3", "hitApi", "Pull tasks and deadlines from project tool", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.asana.com/api/1.0/tasks?project={project_id}&completed_since=now&opt_fields=name,due_on,assignee,notes",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "upcoming_tasks", type: "string" },
          ],
        },
      }),

      // 4. AI meeting analysis
      aiNode(
        "4",
        "AI analyze meeting patterns & suggest optimizations",
        760,
        "You are a productivity consultant. Analyze this weeks calendar events and upcoming tasks. Identify: meetings that could be async, back-to-back meeting clusters without breaks, meetings outside focus hours, low-value recurring meetings, and conflicts with task deadlines. Propose optimization actions: batch similar meetings, add buffer blocks, suggest async alternatives, protect deep-work windows. Score each meeting 1-10 for necessity. Respond JSON: {total_meetings: number, async_candidates: string, back_to_back_clusters: number, focus_time_hours: number, optimization_proposals: string, safe_changes: string, risky_changes: string, estimated_hours_saved: number}",
        "Calendar events: {calendar_events}, Tasks/deadlines: {upcoming_tasks}",
        [
          { field: "total_meetings", as: "total_meetings", type: "number" },
          { field: "async_candidates", as: "async_candidates", type: "string" },
          {
            field: "back_to_back_clusters",
            as: "back_to_back_clusters",
            type: "number",
          },
          { field: "focus_time_hours", as: "focus_time_hours", type: "number" },
          {
            field: "optimization_proposals",
            as: "optimization_proposals",
            type: "string",
          },
          { field: "safe_changes", as: "safe_changes", type: "string" },
          { field: "risky_changes", as: "risky_changes", type: "string" },
          {
            field: "estimated_hours_saved",
            as: "estimated_hours_saved",
            type: "number",
          },
        ],
      ),

      // 5. Free/busy check condition
      n("5", "condition", "Has safe auto-apply changes?", 1000, {
        conditionData: [
          {
            variable: "estimated_hours_saved",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-apply safe changes (add focus blocks)
      n("6", "hitApi", "Auto-create focus blocks on Google Calendar", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "Focus Block (auto-scheduled)" },
            {
              key: "description",
              value:
                "Auto-created by Calendar Optimizer. Safe changes applied: {safe_changes}",
            },
            { key: "colorId", value: "9" },
          ],
          variables: [
            { name: "id", customName: "focus_event_id", type: "string" },
          ],
        },
      }),

      // 7. Draft reschedule requests for risky changes
      aiNode(
        "7",
        "AI draft reschedule request messages",
        1240,
        "You are a professional assistant. Draft polite, specific reschedule request messages for each risky calendar change. Include the reason (better time found, conflict with deadline, etc.) and propose 2 alternative times. Respond JSON: {reschedule_messages: string, message_count: number}",
        "Risky changes: {risky_changes}, Optimization proposals: {optimization_proposals}",
        [
          {
            field: "reschedule_messages",
            as: "reschedule_messages",
            type: "string",
          },
          { field: "message_count", as: "message_count", type: "number" },
        ],
        460,
      ),

      // 8. Weekly summary email
      emailNode(
        "8",
        "Email: Weekly calendar optimization summary",
        1480,
        "{user_email}",
        "Weekly Calendar Optimization — {estimated_hours_saved}h saved",
        "Calendar Optimization Report\\n\\nTotal meetings this week: {total_meetings}\\nFocus time available: {focus_time_hours}h\\nBack-to-back clusters: {back_to_back_clusters}\\nEstimated hours saved: {estimated_hours_saved}h\\n\\nAsync candidates:\\n{async_candidates}\\n\\nSafe changes applied:\\n{safe_changes}\\n\\nReschedule suggestions:\\n{reschedule_messages}\\n\\nOptimization proposals:\\n{optimization_proposals}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // has safe changes -> auto-apply
      e("5", "7", "output_2"), // no safe changes -> draft reschedule only
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Calendar Optimizer** — Weekly analysis of your calendar to reclaim focus time and batch meetings.\n\n1. Runs every Monday morning on schedule\n2. **Google Calendar API** pulls all events for the upcoming week\n3. **Asana API** fetches tasks and deadlines for context\n4. **AI analyzes** meeting patterns, identifies async candidates, and proposes optimizations\n5. Condition checks if safe auto-apply changes exist\n6. **Auto-creates focus blocks** on calendar for safe changes\n7. **AI drafts** polite reschedule messages for risky changes\n8. **Weekly email summary** with hours saved and action items\n\n**To activate:** Say "connect openai", "connect google calendar", "connect asana"`,
  },

  // ===================================================================
  // 52. AI CODE REVIEW AGENT (9 nodes)
  // ===================================================================

  "AI Code Review Agent": {
    nodes: [
      trigger("Webhook: Pull request opened", "webhook_app", {
        source: "GitHub / GitLab / Bitbucket",
        event: "pull_request.opened",
        howItStarts: "A pull request is opened or updated in your repository",
        setup:
          "In GitHub -> Settings -> Webhooks -> Add webhook, select Pull Request events and point to this workflow URL. Or configure GitLab/Bitbucket merge request webhooks.",
        inputFields: [],
      }),

      // 2. Fetch diff and coding standards
      n("2", "hitApi", "Fetch PR diff from GitHub API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/repos/{owner}/{repo}/pulls/{pr_number}",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3.diff" },
          ],
          body: [],
          variables: [
            { name: "body", customName: "pr_diff", type: "string" },
            { name: "title", customName: "pr_title", type: "string" },
            { name: "user.login", customName: "pr_author", type: "string" },
            { name: "head.ref", customName: "branch_name", type: "string" },
          ],
        },
      }),

      // 3. AI code review (correctness, security, performance)
      aiNode(
        "3",
        "AI review: correctness, security & performance",
        520,
        "You are a senior software engineer doing a thorough code review. Analyze this pull request diff for: (1) Correctness — logic errors, edge cases, null handling, race conditions, (2) Security — injection vulnerabilities, auth issues, data exposure, hardcoded secrets, (3) Performance — N+1 queries, unnecessary allocations, missing indexes, expensive operations in loops. For each issue, specify file, line range, severity (critical/high/medium/low), category, and suggested fix. Respond JSON: {issues: string, critical_count: number, high_count: number, medium_count: number, low_count: number, overall_quality: string, summary: string, max_severity: string}",
        "PR: {pr_title} by {pr_author}, Branch: {branch_name}, Diff: {pr_diff}",
        [
          { field: "issues", as: "review_issues", type: "string" },
          { field: "critical_count", as: "critical_count", type: "number" },
          { field: "high_count", as: "high_count", type: "number" },
          { field: "medium_count", as: "medium_count", type: "number" },
          { field: "low_count", as: "low_count", type: "number" },
          { field: "overall_quality", as: "overall_quality", type: "string" },
          { field: "summary", as: "review_summary", type: "string" },
          { field: "max_severity", as: "max_severity", type: "string" },
        ],
      ),

      // 4. Severity condition
      n("4", "condition", "Has critical issues?", 760, {
        conditionData: [
          {
            variable: "critical_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Critical: block merge + alert Slack
      n(
        "5",
        "hitApi",
        "Block merge: post review + request changes",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.github.com/repos/{owner}/{repo}/pulls/{pr_number}/reviews",
            headers: [
              { key: "Authorization", value: "Bearer {github_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "event", value: "REQUEST_CHANGES" },
              {
                key: "body",
                value:
                  "AI Code Review — CRITICAL ISSUES FOUND\\n\\n{review_summary}\\n\\nCritical: {critical_count} | High: {high_count} | Medium: {medium_count}\\n\\nDetails:\\n{review_issues}",
              },
            ],
            variables: [
              { name: "id", customName: "review_id", type: "string" },
            ],
          },
        },
        460,
      ),

      // 6. Medium/low: post change request comments
      n("6", "hitApi", "Post review comments on PR", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/repos/{owner}/{repo}/pulls/{pr_number}/reviews",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event", value: "COMMENT" },
            {
              key: "body",
              value:
                "AI Code Review — {overall_quality}\\n\\n{review_summary}\\n\\nHigh: {high_count} | Medium: {medium_count} | Low: {low_count}\\n\\nSuggestions:\\n{review_issues}",
            },
          ],
          variables: [
            { name: "id", customName: "comment_review_id", type: "string" },
          ],
        },
      }),

      // 7. DM author on Slack
      slackNode(
        "7",
        "Slack DM: notify PR author",
        1240,
        "Code review complete for PR *{pr_title}* by {pr_author}\\nBranch: {branch_name}\\nQuality: {overall_quality}\\nCritical: {critical_count} | High: {high_count} | Medium: {medium_count} | Low: {low_count}\\nSummary: {review_summary}",
      ),

      // 8. Static analysis comparison
      n("8", "hitApi", "Run SonarQube static analysis comparison", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://sonarqube.your-company.com/api/measures/component?component={repo}&metricKeys=bugs,vulnerabilities,code_smells,coverage&branch={branch_name}",
          headers: [
            { key: "Authorization", value: "Bearer {sonar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "component.measures",
              customName: "static_analysis_results",
              type: "string",
            },
          ],
        },
      }),

      // 9. Metrics logging
      n("9", "hitApi", "Log review metrics to analytics API", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/code-review/metrics",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "pr_number", value: "{pr_number}" },
            { key: "author", value: "{pr_author}" },
            { key: "critical_count", value: "{critical_count}" },
            { key: "high_count", value: "{high_count}" },
            { key: "medium_count", value: "{medium_count}" },
            { key: "overall_quality", value: "{overall_quality}" },
            { key: "static_analysis", value: "{static_analysis_results}" },
          ],
          variables: [
            { name: "id", customName: "metric_log_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> block merge
      e("4", "6", "output_2"), // non-critical -> post comments
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Code Review Agent** — Automated code review for correctness, security, and performance on every PR.\n\n1. Pull request webhook fires from GitHub/GitLab\n2. **GitHub API** fetches the full PR diff and metadata\n3. **AI reviews** for correctness, security vulnerabilities, and performance issues\n4. Severity condition routes critical vs. non-critical findings\n5. Critical issues: **merge blocked** with REQUEST_CHANGES review\n6. Non-critical: **comments posted** with suggestions\n7. **Slack DM** notifies the PR author with summary\n8. **SonarQube** static analysis provides comparison data\n9. All metrics **logged** for team-level code quality tracking\n\n**To activate:** Say "connect openai", "connect github", "connect slack", "connect sonarqube"`,
  },

  // ===================================================================
  // 53. AI COMPLIANCE CHECKER (8 nodes)
  // ===================================================================

  "AI Compliance Checker": {
    nodes: [
      trigger(
        "Webhook: Document submitted for compliance review",
        "webhook_app",
        {
          source: "Document Management / SharePoint / Legal Portal",
          event: "document.submitted",
          howItStarts:
            "A document is submitted for compliance review — policies, contracts, marketing materials, or data handling procedures",
          setup:
            "Configure your document management system to POST documents to this workflow URL when they enter the compliance review stage. Include the document content and metadata.",
          inputFields: [],
        },
      ),

      // 2. Extract document and load rule pack
      aiNode(
        "2",
        "AI extract document content & metadata",
        280,
        "Read this document submission payload and extract the content regardless of format. Find the document text, document type (policy/contract/marketing/data-handling), department, author, and any compliance tags. Respond JSON: {document_text: string, document_type: string, department: string, author_email: string, doc_title: string, compliance_tags: string}",
        "{webhook_payload}",
        [
          { field: "document_text", as: "document_text", type: "string" },
          { field: "document_type", as: "document_type", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "doc_title", as: "doc_title", type: "string" },
          { field: "compliance_tags", as: "compliance_tags", type: "string" },
        ],
      ),

      // 3. Load compliance rule pack
      n("3", "hitApi", "Fetch compliance rule pack from config API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-company.com/compliance/rules?type={document_type}&tags={compliance_tags}",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "rules", customName: "compliance_rules", type: "string" },
            { name: "framework", customName: "framework_name", type: "string" },
            { name: "total_rules", customName: "total_rules", type: "number" },
          ],
        },
      }),

      // 4. AI section-aware compliance analysis
      aiNode(
        "4",
        "AI analyze compliance rule-by-rule",
        760,
        "You are a compliance analyst. Review this document section-by-section against the compliance rule pack. For each rule, determine: pass/fail/partial, the relevant document section, specific text that violates or satisfies the rule, and remediation guidance if failing. Assign severity: critical (legal exposure), high (regulatory risk), medium (best practice), low (recommendation). Respond JSON: {rule_results: string, pass_count: number, fail_count: number, partial_count: number, critical_failures: number, overall_compliance_pct: number, max_severity: string, remediation_summary: string}",
        "Document: {document_text}, Title: {doc_title}, Type: {document_type}, Rules: {compliance_rules}, Framework: {framework_name}",
        [
          { field: "rule_results", as: "rule_results", type: "string" },
          { field: "pass_count", as: "pass_count", type: "number" },
          { field: "fail_count", as: "fail_count", type: "number" },
          { field: "partial_count", as: "partial_count", type: "number" },
          {
            field: "critical_failures",
            as: "critical_failures",
            type: "number",
          },
          {
            field: "overall_compliance_pct",
            as: "overall_compliance_pct",
            type: "number",
          },
          { field: "max_severity", as: "max_severity", type: "string" },
          {
            field: "remediation_summary",
            as: "remediation_summary",
            type: "string",
          },
        ],
      ),

      // 5. Severity condition
      n("5", "condition", "Has critical compliance failures?", 1000, {
        conditionData: [
          {
            variable: "critical_failures",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Critical: escalate to legal
      slackNode(
        "6",
        "Escalate to legal: critical compliance failure",
        1240,
        "CRITICAL COMPLIANCE FAILURE\\nDocument: *{doc_title}* ({document_type})\\nDepartment: {department}\\nAuthor: {author_email}\\nFramework: {framework_name}\\nCritical failures: {critical_failures}\\nOverall compliance: {overall_compliance_pct}%\\n\\nRemediation needed:\\n{remediation_summary}",
        460,
      ),

      // 7. Create remediation tasks
      n("7", "hitApi", "Create remediation tasks in Asana", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Compliance Remediation: {doc_title}","notes":"Document: {doc_title}\\nType: {document_type}\\nCompliance: {overall_compliance_pct}%\\nFailed rules: {fail_count}\\nCritical: {critical_failures}\\n\\nRemediation:\\n{remediation_summary}","projects":["{compliance_project_id}"],"assignee":"{author_email}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "task_id", type: "string" },
          ],
        },
      }),

      // 8. Immutable archive
      n("8", "hitApi", "Archive compliance report to S3", 1720, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{s3_bucket}.s3.amazonaws.com/compliance-reports/{doc_title}-{timestamp}.json",
          headers: [
            { key: "Authorization", value: "{aws_sig_v4}" },
            { key: "Content-Type", value: "application/json" },
            { key: "x-amz-storage-class", value: "GLACIER_IR" },
          ],
          body: [
            { key: "document_title", value: "{doc_title}" },
            { key: "document_type", value: "{document_type}" },
            { key: "framework", value: "{framework_name}" },
            { key: "compliance_pct", value: "{overall_compliance_pct}" },
            { key: "rule_results", value: "{rule_results}" },
            { key: "remediation", value: "{remediation_summary}" },
          ],
          variables: [
            { name: "ETag", customName: "archive_etag", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> escalate to legal
      e("5", "7", "output_2"), // non-critical -> create remediation tasks
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Compliance Checker** — Analyzes documents rule-by-rule against compliance frameworks and escalates critical failures.\n\n1. Document submitted via webhook for compliance review\n2. **AI extracts** document content, type, and metadata\n3. **Compliance API** loads the relevant rule pack and framework\n4. **AI analyzes** each section against each rule with pass/fail/partial status\n5. Severity gate routes critical failures to escalation\n6. Critical: **Slack alert** to legal team with remediation summary\n7. **Asana tasks** created for all remediation items assigned to author\n8. Full report **archived** to S3 Glacier for audit trail\n\n**To activate:** Say "connect openai", "connect your compliance API", "connect slack", "connect asana", "connect aws s3"`,
  },

  // ===================================================================
  // 54. AI CONTENT WRITER (9 nodes)
  // ===================================================================

  "AI Content Writer": {
    nodes: [
      trigger("Webhook: Content brief submitted", "webhook_app", {
        source: "Content Calendar / CMS / Trello",
        event: "brief.created",
        howItStarts:
          "A content brief is submitted with topic, keywords, audience, and publication details",
        setup:
          "Configure your content calendar or project tool to POST brief data to this workflow URL when a new content piece is assigned.",
        inputFields: [],
      }),

      // 2. Extract brief and research SERP
      aiNode(
        "2",
        "AI extract content brief",
        280,
        "Read this content brief payload and extract all details regardless of format. Find the topic, primary keyword, secondary keywords, target audience, target word count, content type (blog/landing/whitepaper), tone, and CMS slug. Respond JSON: {topic: string, primary_keyword: string, secondary_keywords: string, audience: string, word_count: string, content_type: string, tone: string, cms_slug: string, author_email: string}",
        "{webhook_payload}",
        [
          { field: "topic", as: "topic", type: "string" },
          { field: "primary_keyword", as: "primary_keyword", type: "string" },
          {
            field: "secondary_keywords",
            as: "secondary_keywords",
            type: "string",
          },
          { field: "audience", as: "audience", type: "string" },
          { field: "word_count", as: "word_count", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "tone", as: "tone", type: "string" },
          { field: "cms_slug", as: "cms_slug", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
        ],
      ),

      // 3. SERP research
      n("3", "hitApi", "SerpAPI: Research competing content", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={primary_keyword}&num=10&api_key={serpapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "serp_results",
              type: "string",
            },
            {
              name: "related_questions",
              customName: "people_also_ask",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI write section-by-section draft
      aiNode(
        "4",
        "AI write full content draft",
        760,
        "You are an expert content writer. Write a comprehensive, section-by-section draft based on the brief and SERP research. Include: engaging intro with hook, H2/H3 structured sections, data points, examples, and a strong CTA. Naturally weave in keywords. Match the specified tone. Include a meta description under 160 chars. Respond JSON: {full_draft: string, meta_description: string, actual_word_count: number, keyword_density: number, sections_count: number, readability_score: string}",
        "Topic: {topic}, Keyword: {primary_keyword}, Secondary: {secondary_keywords}, Audience: {audience}, Target words: {word_count}, Type: {content_type}, Tone: {tone}, SERP: {serp_results}, PAA: {people_also_ask}",
        [
          { field: "full_draft", as: "full_draft", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          {
            field: "actual_word_count",
            as: "actual_word_count",
            type: "number",
          },
          { field: "keyword_density", as: "keyword_density", type: "number" },
          { field: "sections_count", as: "sections_count", type: "number" },
          {
            field: "readability_score",
            as: "readability_score",
            type: "string",
          },
        ],
      ),

      // 5. Plagiarism check condition
      n("5", "hitApi", "Copyscape plagiarism check", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.copyscape.com/api/?u={copyscape_user}&o={copyscape_api_key}&t={full_draft}&f=json",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "result",
              customName: "plagiarism_results",
              type: "string",
            },
            {
              name: "percentmatched",
              customName: "plagiarism_pct",
              type: "number",
            },
          ],
        },
      }),

      // 6. Plagiarism condition
      n("6", "condition", "Plagiarism below 5%?", 1240, {
        conditionData: [
          {
            variable: "plagiarism_pct",
            value: "5",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SEO optimization + brand/compliance pass
      aiNode(
        "7",
        "AI SEO optimization & brand compliance pass",
        1480,
        "You are an SEO and brand compliance editor. Optimize this draft for search engines: ensure keyword placement in first 100 words, H2s, and conclusion. Check for brand compliance issues, legal disclaimers needed, and accessibility of language. Suggest internal links. Respond JSON: {optimized_draft: string, seo_changes: string, compliance_notes: string, internal_link_suggestions: string, final_word_count: number, seo_score: number}",
        "Draft: {full_draft}, Keyword: {primary_keyword}, Secondary: {secondary_keywords}, Tone: {tone}, Audience: {audience}",
        [
          { field: "optimized_draft", as: "optimized_draft", type: "string" },
          { field: "seo_changes", as: "seo_changes", type: "string" },
          { field: "compliance_notes", as: "compliance_notes", type: "string" },
          {
            field: "internal_link_suggestions",
            as: "internal_link_suggestions",
            type: "string",
          },
          { field: "final_word_count", as: "final_word_count", type: "number" },
          { field: "seo_score", as: "seo_score", type: "number" },
        ],
      ),

      // 8. Push to CMS as draft
      n("8", "hitApi", "Create CMS draft in WordPress", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wordpress_site}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wordpress_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{topic}" },
            { key: "content", value: "{optimized_draft}" },
            { key: "slug", value: "{cms_slug}" },
            { key: "status", value: "draft" },
            { key: "excerpt", value: "{meta_description}" },
          ],
          variables: [
            { name: "id", customName: "cms_post_id", type: "string" },
            { name: "link", customName: "cms_preview_url", type: "string" },
          ],
        },
      }),

      // 9. Notify editor
      emailNode(
        "9",
        "Email editor: draft ready for review",
        1960,
        "{author_email}",
        "Content Draft Ready: {topic} — SEO Score {seo_score}/100",
        "Hi,\\n\\nA new content draft is ready for your review:\\n\\nTopic: {topic}\\nWord count: {final_word_count}\\nSEO Score: {seo_score}/100\\nReadability: {readability_score}\\nPlagiarism: {plagiarism_pct}%\\n\\nPreview: {cms_preview_url}\\n\\nSEO changes made:\\n{seo_changes}\\n\\nCompliance notes:\\n{compliance_notes}\\n\\nInternal link suggestions:\\n{internal_link_suggestions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // pass plagiarism -> SEO optimize
      e("6", "4", "output_2"), // fail plagiarism -> rewrite
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Content Writer** — End-to-end content creation from brief to CMS draft with SEO and plagiarism checks.\n\n1. Content brief submitted via webhook\n2. **AI extracts** topic, keywords, audience, and requirements\n3. **SerpAPI** researches competing content and People Also Ask\n4. **AI writes** full section-by-section draft with proper structure\n5. **Copyscape** plagiarism check ensures originality\n6. Plagiarism gate blocks content above 5% match\n7. **AI SEO pass** optimizes keywords, adds internal links, checks compliance\n8. **WordPress API** creates CMS draft with meta description\n9. **Email notification** to editor with preview link and scores\n\n**To activate:** Say "connect openai", "connect serpapi", "connect copyscape", "connect wordpress"`,
  },

  // ===================================================================
  // 55. AI CONTRACT NEGOTIATION (9 nodes)
  // ===================================================================

  "AI Contract Negotiation": {
    nodes: [
      trigger("Webhook: Contract submitted for review", "webhook_app", {
        source: "DocuSign / Legal Portal / Email",
        event: "contract.submitted",
        howItStarts:
          "A counterparty contract is submitted for AI-assisted negotiation analysis and redlining",
        setup:
          "Configure your legal intake portal or DocuSign to POST contract documents to this workflow URL. Or forward contract emails to the workflow trigger address.",
        inputFields: [],
      }),

      // 2. Extract contract text
      aiNode(
        "2",
        "AI extract contract text & metadata",
        280,
        "Read this contract submission payload and extract all details regardless of format. Find the contract text, counterparty name, contract type (NDA/MSA/SaaS/employment/vendor), deal value, submitter email, and urgency. Respond JSON: {contract_text: string, counterparty: string, contract_type: string, deal_value: string, submitter_email: string, urgency: string, effective_date: string}",
        "{webhook_payload}",
        [
          { field: "contract_text", as: "contract_text", type: "string" },
          { field: "counterparty", as: "counterparty", type: "string" },
          { field: "contract_type", as: "contract_type", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "effective_date", as: "effective_date", type: "string" },
        ],
      ),

      // 3. Load negotiation playbook
      n("3", "hitApi", "Fetch negotiation playbook from legal config", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-company.com/legal/playbook?type={contract_type}",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "playbook.preferred_terms",
              customName: "preferred_terms",
              type: "string",
            },
            {
              name: "playbook.red_lines",
              customName: "red_lines",
              type: "string",
            },
            {
              name: "playbook.fallback_positions",
              customName: "fallback_positions",
              type: "string",
            },
            {
              name: "playbook.approval_thresholds",
              customName: "approval_thresholds",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI clause-by-clause analysis
      aiNode(
        "4",
        "AI analyze clauses against playbook",
        760,
        "You are a corporate attorney. Analyze this contract clause-by-clause against the negotiation playbook. For each clause: identify it by name, assess risk level (red/yellow/green), compare to preferred terms, note deviations, and suggest redline language. Pay special attention to: liability caps, indemnification, IP ownership, termination rights, governing law, data processing, and non-compete provisions. Respond JSON: {clause_analysis: string, red_count: number, yellow_count: number, green_count: number, overall_risk: string, key_deviations: string, redline_suggestions: string, deal_breakers: string}",
        "Contract: {contract_text}, Type: {contract_type}, Counterparty: {counterparty}, Preferred terms: {preferred_terms}, Red lines: {red_lines}, Fallback positions: {fallback_positions}",
        [
          { field: "clause_analysis", as: "clause_analysis", type: "string" },
          { field: "red_count", as: "red_count", type: "number" },
          { field: "yellow_count", as: "yellow_count", type: "number" },
          { field: "green_count", as: "green_count", type: "number" },
          { field: "overall_risk", as: "overall_risk", type: "string" },
          { field: "key_deviations", as: "key_deviations", type: "string" },
          {
            field: "redline_suggestions",
            as: "redline_suggestions",
            type: "string",
          },
          { field: "deal_breakers", as: "deal_breakers", type: "string" },
        ],
      ),

      // 5. Risk condition
      n("5", "condition", "Has deal-breaker red clauses?", 1000, {
        conditionData: [
          {
            variable: "red_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Escalate to legal for red-risk
      slackNode(
        "6",
        "Alert legal: deal-breaker clauses found",
        1240,
        "CONTRACT REVIEW ALERT — Deal-breaker clauses found\\nCounterparty: *{counterparty}*\\nType: {contract_type} | Value: {deal_value}\\nRisk: {overall_risk}\\nRed clauses: {red_count} | Yellow: {yellow_count}\\n\\nDeal breakers:\\n{deal_breakers}\\n\\nKey deviations:\\n{key_deviations}",
        460,
      ),

      // 7. Generate redline document
      aiNode(
        "7",
        "AI generate redline document",
        1480,
        "You are a legal editor. Generate a complete redline version of this contract with all suggested changes marked. Use standard redline notation: [DELETED: original text] [INSERTED: new text]. Include margin comments explaining each change and the negotiation rationale from the playbook. Respond JSON: {redline_document: string, total_changes: number, change_summary: string, negotiation_talking_points: string}",
        "Original contract: {contract_text}, Clause analysis: {clause_analysis}, Redline suggestions: {redline_suggestions}, Fallback positions: {fallback_positions}",
        [
          { field: "redline_document", as: "redline_document", type: "string" },
          { field: "total_changes", as: "total_changes", type: "number" },
          { field: "change_summary", as: "change_summary", type: "string" },
          {
            field: "negotiation_talking_points",
            as: "negotiation_talking_points",
            type: "string",
          },
        ],
      ),

      // 8. Notify legal and commercial
      emailNode(
        "8",
        "Email: contract review complete",
        1720,
        "{submitter_email}",
        "Contract Review: {counterparty} {contract_type} — {overall_risk} risk",
        "Contract Review Complete\\n\\nCounterparty: {counterparty}\\nType: {contract_type} | Value: {deal_value}\\nOverall Risk: {overall_risk}\\nRed: {red_count} | Yellow: {yellow_count} | Green: {green_count}\\n\\nKey Deviations:\\n{key_deviations}\\n\\nTotal Redline Changes: {total_changes}\\nChange Summary:\\n{change_summary}\\n\\nNegotiation Talking Points:\\n{negotiation_talking_points}\\n\\nPlease review the redline document and schedule a negotiation call.",
      ),

      // 9. Log clause trends
      n("9", "hitApi", "Log clause trends to analytics", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/legal/clause-trends",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "counterparty", value: "{counterparty}" },
            { key: "contract_type", value: "{contract_type}" },
            { key: "deal_value", value: "{deal_value}" },
            { key: "red_count", value: "{red_count}" },
            { key: "yellow_count", value: "{yellow_count}" },
            { key: "overall_risk", value: "{overall_risk}" },
            { key: "clause_analysis", value: "{clause_analysis}" },
          ],
          variables: [
            { name: "id", customName: "trend_log_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // red clauses -> escalate to legal
      e("5", "7", "output_2"), // no red clauses -> generate redline
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Contract Negotiation** — Analyzes contracts clause-by-clause against your playbook and generates redline documents.\n\n1. Contract submitted via webhook from DocuSign or legal portal\n2. **AI extracts** contract text, counterparty, type, and deal value\n3. **Playbook API** loads preferred terms, red lines, and fallback positions\n4. **AI analyzes** each clause for risk, deviations, and redline suggestions\n5. Risk gate identifies deal-breaker clauses\n6. Red clauses: **Slack alert** to legal team\n7. **AI generates** full redline document with change explanations\n8. **Email** to submitter with review summary and talking points\n9. Clause trend data **logged** for negotiation analytics\n\n**To activate:** Say "connect openai", "connect your legal API", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 56. AI CUSTOMER JOURNEY OPTIMIZER (10 nodes)
  // ===================================================================

  "AI Customer Journey Optimizer": {
    nodes: [
      trigger("Schedule: Weekly journey analysis", "schedule", {
        schedule: "Every Monday at 6:00 AM",
        howItStarts:
          "Runs weekly to analyze customer journey data, identify bottlenecks, and trigger interventions",
        setup:
          "Connect your event analytics platform and CRM. The workflow pulls journey events, computes metrics, and creates targeted interventions.",
        inputFields: [],
      }),

      // 2. Pull event stream
      n("2", "hitApi", "Pull journey events from Segment/Mixpanel", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://mixpanel.com/api/2.0/jql",
          headers: [
            { key: "Authorization", value: "Basic {mixpanel_secret_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "script",
              value:
                "function main(){return Events({from_date:'{week_ago}',to_date:'{today}'}).groupByUser(['event','properties.$current_url']).reduce(function(a,events){return events})}",
            },
          ],
          variables: [
            { name: "results", customName: "journey_events", type: "string" },
          ],
        },
      }),

      // 3. CRM enrichment
      n("3", "hitApi", "Enrich with CRM customer data", 520, {
        hitApiDetails: {
          method: "GET",
          url: 'https://api.hubspot.com/crm/v3/objects/contacts?limit=100&properties=email,lifecyclestage,hs_lead_status,recent_deal_amount&filterGroups=[{"filters":[{"propertyName":"lastmodifieddate","operator":"GTE","value":"{week_ago_ms}"}]}]',
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "crm_contacts", type: "string" },
            { name: "total", customName: "total_contacts", type: "number" },
          ],
        },
      }),

      // 4. AI compute journey metrics
      aiNode(
        "4",
        "AI compute journey metrics & identify bottlenecks",
        760,
        "You are a customer journey analyst. Analyze these journey events enriched with CRM data. Compute: conversion rates between each stage, average time between stages, drop-off points, re-engagement patterns, and cohort differences. Identify the top 3 bottlenecks with quantified impact (lost revenue estimate). Respond JSON: {stage_metrics: string, bottleneck_1: string, bottleneck_2: string, bottleneck_3: string, total_dropoffs: number, estimated_revenue_at_risk: number, avg_journey_days: number, conversion_rate_pct: number}",
        "Journey events: {journey_events}, CRM data: {crm_contacts}, Total contacts: {total_contacts}",
        [
          { field: "stage_metrics", as: "stage_metrics", type: "string" },
          { field: "bottleneck_1", as: "bottleneck_1", type: "string" },
          { field: "bottleneck_2", as: "bottleneck_2", type: "string" },
          { field: "bottleneck_3", as: "bottleneck_3", type: "string" },
          { field: "total_dropoffs", as: "total_dropoffs", type: "number" },
          {
            field: "estimated_revenue_at_risk",
            as: "estimated_revenue_at_risk",
            type: "number",
          },
          { field: "avg_journey_days", as: "avg_journey_days", type: "number" },
          {
            field: "conversion_rate_pct",
            as: "conversion_rate_pct",
            type: "number",
          },
        ],
      ),

      // 5. Risk bucketing condition
      n("5", "condition", "Revenue at risk > $10,000?", 1000, {
        conditionData: [
          {
            variable: "estimated_revenue_at_risk",
            value: "10000",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. High risk: create CSM tasks
      n(
        "6",
        "hitApi",
        "Create CSM intervention tasks in Asana",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://app.asana.com/api/1.0/tasks",
            headers: [
              { key: "Authorization", value: "Bearer {asana_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "data",
                value:
                  '{"name":"Journey Intervention: {bottleneck_1}","notes":"Revenue at risk: ${estimated_revenue_at_risk}\\nDropoffs: {total_dropoffs}\\n\\nBottleneck 1: {bottleneck_1}\\nBottleneck 2: {bottleneck_2}\\nBottleneck 3: {bottleneck_3}\\n\\nAction: Manual CSM outreach required","projects":["{csm_project_id}"],"tags":["high-priority"]}',
              },
            ],
            variables: [
              { name: "data.gid", customName: "csm_task_id", type: "string" },
            ],
          },
        },
        460,
      ),

      // 7. Medium risk: auto-nudge emails
      aiNode(
        "7",
        "AI craft personalized nudge emails",
        1240,
        "You are a customer success specialist. Create personalized nudge email templates for customers stuck at each bottleneck stage. Each email should: acknowledge the customer stage, offer value (resource/demo/case study), and include a clear CTA. Respond JSON: {nudge_emails: string, email_count: number, personalization_fields: string}",
        "Bottleneck 1: {bottleneck_1}, Bottleneck 2: {bottleneck_2}, Bottleneck 3: {bottleneck_3}, Stage metrics: {stage_metrics}",
        [
          { field: "nudge_emails", as: "nudge_emails", type: "string" },
          { field: "email_count", as: "email_count", type: "number" },
          {
            field: "personalization_fields",
            as: "personalization_fields",
            type: "string",
          },
        ],
      ),

      // 8. CRM writeback
      n("8", "hitApi", "Update HubSpot contacts with journey scores", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/batch/update",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "inputs",
              value:
                '[{"id":"{contact_id}","properties":{"journey_risk_score":"{estimated_revenue_at_risk}","journey_stage_bottleneck":"{bottleneck_1}","last_journey_analysis":"{today}"}}]',
            },
          ],
          variables: [
            { name: "status", customName: "crm_update_status", type: "string" },
          ],
        },
      }),

      // 9. Weekly cohort report
      aiNode(
        "9",
        "AI generate weekly cohort report",
        1720,
        "You are a data analyst. Generate a concise weekly cohort report from the journey analysis. Include: week-over-week conversion changes, new bottleneck vs persistent bottleneck comparison, revenue impact trend, and top 3 recommended actions. Format as executive-friendly bullet points. Respond JSON: {cohort_report: string, wow_change_pct: number, top_action: string, report_headline: string}",
        "Stage metrics: {stage_metrics}, Bottlenecks: {bottleneck_1}, {bottleneck_2}, {bottleneck_3}, Revenue at risk: {estimated_revenue_at_risk}, Conversion: {conversion_rate_pct}%",
        [
          { field: "cohort_report", as: "cohort_report", type: "string" },
          { field: "wow_change_pct", as: "wow_change_pct", type: "number" },
          { field: "top_action", as: "top_action", type: "string" },
          { field: "report_headline", as: "report_headline", type: "string" },
        ],
      ),

      // 10. Effectiveness measurement email
      emailNode(
        "10",
        "Email: Weekly journey optimization report",
        1960,
        "{team_email}",
        "Journey Report: {report_headline} — {conversion_rate_pct}% conversion",
        "Weekly Customer Journey Optimization Report\\n\\n{report_headline}\\n\\nKey Metrics:\\n- Conversion rate: {conversion_rate_pct}%\\n- WoW change: {wow_change_pct}%\\n- Revenue at risk: ${estimated_revenue_at_risk}\\n- Total dropoffs: {total_dropoffs}\\n- Avg journey: {avg_journey_days} days\\n\\nTop Bottlenecks:\\n1. {bottleneck_1}\\n2. {bottleneck_2}\\n3. {bottleneck_3}\\n\\nCohort Report:\\n{cohort_report}\\n\\nTop Action: {top_action}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high risk -> CSM tasks
      e("5", "7", "output_2"), // medium risk -> auto nudge
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
    summary: `**AI Customer Journey Optimizer** — Weekly analysis of journey bottlenecks with automated CSM interventions.\n\n1. Runs every Monday on schedule\n2. **Mixpanel** pulls journey events for the past week\n3. **HubSpot CRM** enriches with customer lifecycle data\n4. **AI computes** stage metrics, drop-offs, and bottleneck impact\n5. Revenue-at-risk gate routes high vs. medium risk\n6. High risk: **Asana CSM tasks** for manual intervention\n7. Medium risk: **AI-crafted nudge emails** for auto-engagement\n8. **HubSpot writeback** updates journey risk scores on contacts\n9. **AI generates** weekly cohort report with WoW trends\n10. **Email report** distributed to the team\n\n**To activate:** Say "connect openai", "connect mixpanel", "connect hubspot", "connect asana"`,
  },

  // ===================================================================
  // 57. AI CUSTOMER PERSONA GENERATOR (8 nodes)
  // ===================================================================

  "AI Customer Persona Generator": {
    nodes: [
      trigger("Schedule: Monthly persona refresh", "schedule", {
        schedule: "First Monday of each month at 8:00 AM",
        howItStarts:
          "Runs monthly to aggregate customer data, cluster behaviors, and generate updated buyer personas",
        setup:
          "Connect your CRM, analytics, and support platforms. The workflow aggregates data across sources and uses AI to generate data-driven personas.",
        inputFields: [],
      }),

      // 2. Aggregate data from multiple sources
      n("2", "hitApi", "Pull customer data from CRM + analytics", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "limit", value: "200" },
            {
              key: "properties",
              value:
                '["email","industry","company_size","jobtitle","lifecyclestage","hs_analytics_source","recent_deal_amount","num_associated_deals","hs_lead_status"]',
            },
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"createdate","operator":"GTE","value":"{six_months_ago_ms}"}]}]',
            },
          ],
          variables: [
            { name: "results", customName: "crm_customers", type: "string" },
            { name: "total", customName: "total_records", type: "number" },
          ],
        },
      }),

      // 3. AI clean and standardize data
      aiNode(
        "3",
        "AI clean & standardize customer data",
        520,
        "You are a data engineer. Clean and standardize this customer dataset: normalize job titles into categories (C-suite, VP, Director, Manager, IC), standardize industry names, bucket company sizes (SMB: 1-50, Mid-market: 51-500, Enterprise: 501+), and flag incomplete records. Respond JSON: {cleaned_records: string, total_cleaned: number, incomplete_count: number, industry_distribution: string, role_distribution: string, size_distribution: string}",
        "Raw CRM data: {crm_customers}, Total records: {total_records}",
        [
          { field: "cleaned_records", as: "cleaned_records", type: "string" },
          { field: "total_cleaned", as: "total_cleaned", type: "number" },
          { field: "incomplete_count", as: "incomplete_count", type: "number" },
          {
            field: "industry_distribution",
            as: "industry_distribution",
            type: "string",
          },
          {
            field: "role_distribution",
            as: "role_distribution",
            type: "string",
          },
          {
            field: "size_distribution",
            as: "size_distribution",
            type: "string",
          },
        ],
      ),

      // 4. AI clustering and persona generation
      aiNode(
        "4",
        "AI cluster customers & generate personas",
        760,
        "You are a marketing strategist. Analyze this cleaned customer dataset and identify 3-5 distinct buyer personas using behavioral and demographic clustering. For each persona provide: name (creative + descriptive), demographic profile (role, industry, company size), behavioral patterns (buying triggers, preferred channels, content preferences), pain points, goals, objections, average deal size, and typical journey length. Respond JSON: {personas: string, persona_count: number, total_customers_mapped: number, unmapped_pct: number, primary_persona_name: string}",
        "Cleaned data: {cleaned_records}, Industries: {industry_distribution}, Roles: {role_distribution}, Sizes: {size_distribution}",
        [
          { field: "personas", as: "personas", type: "string" },
          { field: "persona_count", as: "persona_count", type: "number" },
          {
            field: "total_customers_mapped",
            as: "total_customers_mapped",
            type: "number",
          },
          { field: "unmapped_pct", as: "unmapped_pct", type: "number" },
          {
            field: "primary_persona_name",
            as: "primary_persona_name",
            type: "string",
          },
        ],
      ),

      // 5. AI create proof points and boundaries
      aiNode(
        "5",
        "AI create proof points & persona boundaries",
        1000,
        "You are a research analyst. For each buyer persona, create: (1) 3 proof points — specific data from the dataset that validates this persona exists, (2) boundary definitions — what distinguishes this persona from adjacent personas, (3) anti-personas — who this is NOT, (4) recommended messaging angles for each persona. Respond JSON: {proof_points: string, boundary_definitions: string, anti_personas: string, messaging_angles: string, confidence_scores: string}",
        "Personas: {personas}, Persona count: {persona_count}, Data: {cleaned_records}",
        [
          { field: "proof_points", as: "proof_points", type: "string" },
          {
            field: "boundary_definitions",
            as: "boundary_definitions",
            type: "string",
          },
          { field: "anti_personas", as: "anti_personas", type: "string" },
          { field: "messaging_angles", as: "messaging_angles", type: "string" },
          {
            field: "confidence_scores",
            as: "confidence_scores",
            type: "string",
          },
        ],
      ),

      // 6. Publish persona page to Notion
      n("6", "hitApi", "Publish personas to Notion workspace", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{persona_db_id}"}' },
            {
              key: "properties",
              value:
                '{"Title":{"title":[{"text":{"content":"Buyer Personas — {today}"}}]},"Persona Count":{"number":{persona_count}},"Primary Persona":{"rich_text":[{"text":{"content":"{primary_persona_name}"}}]},"Customers Mapped":{"number":{total_customers_mapped}}}',
            },
            {
              key: "children",
              value:
                '[{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Personas"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{personas}"}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Proof Points"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{proof_points}"}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Messaging Angles"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{messaging_angles}"}}]}}]',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),

      // 7. Boundary definition archive
      n("7", "hitApi", "Archive persona data to analytics warehouse", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/personas/archive",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "personas", value: "{personas}" },
            { key: "proof_points", value: "{proof_points}" },
            { key: "boundaries", value: "{boundary_definitions}" },
            { key: "confidence", value: "{confidence_scores}" },
            { key: "data_points", value: "{total_cleaned}" },
          ],
          variables: [{ name: "id", customName: "archive_id", type: "string" }],
        },
      }),

      // 8. Distribution notification
      slackNode(
        "8",
        "Notify team: updated personas published",
        1720,
        "Updated buyer personas published\\n{persona_count} personas identified from {total_customers_mapped} customers\\nPrimary: *{primary_persona_name}*\\nUnmapped: {unmapped_pct}%\\n\\nNotion page: https://notion.so/{notion_page_id}\\n\\nMessaging angles and proof points included.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Customer Persona Generator** — Monthly data-driven persona generation from CRM and behavioral data.\n\n1. Runs first Monday of each month on schedule\n2. **HubSpot CRM** pulls customer data with demographics and deal history\n3. **AI cleans** and standardizes job titles, industries, and company sizes\n4. **AI clusters** customers into 3-5 distinct buyer personas\n5. **AI creates** proof points, boundary definitions, and messaging angles\n6. Personas **published** to Notion workspace with full documentation\n7. Historical persona data **archived** for trend analysis\n8. **Slack notification** alerts the team with persona summary\n\n**To activate:** Say "connect openai", "connect hubspot", "connect notion", "connect slack"`,
  },

  // ===================================================================
  // 58. AI CUSTOMER RESPONSE (7 nodes)
  // ===================================================================

  "AI Customer Response": {
    nodes: [
      trigger("Webhook: New support ticket created", "webhook_app", {
        source: "Zendesk / Intercom / Freshdesk",
        event: "ticket.created",
        howItStarts:
          "A new customer support ticket is created with a question or issue",
        setup:
          "In Zendesk -> Admin -> Webhooks, create a webhook pointing to this workflow URL triggered on ticket creation. Or configure Intercom/Freshdesk webhook triggers.",
        inputFields: [],
      }),

      // 2. Extract ticket + classify intent
      aiNode(
        "2",
        "AI classify intent, urgency & sentiment",
        280,
        "Read this support ticket payload and extract all details regardless of format. Classify the ticket into: intent (question/bug/feature-request/billing/account), urgency (critical/high/medium/low), sentiment (positive/neutral/frustrated/angry). Also find the customer email and name. Respond JSON: {ticket_text: string, customer_email: string, customer_name: string, intent: string, urgency: string, sentiment: string, product_area: string, ticket_id: string}",
        "{webhook_payload}",
        [
          { field: "ticket_text", as: "ticket_text", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "intent", as: "intent", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "ticket_id", as: "ticket_id", type: "string" },
        ],
      ),

      // 3. KB retrieval
      n("3", "hitApi", "Search knowledge base for relevant articles", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/kb/search",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query", value: "{ticket_text}" },
            { key: "product_area", value: "{product_area}" },
            { key: "limit", value: "5" },
          ],
          variables: [
            { name: "results", customName: "kb_articles", type: "string" },
            {
              name: "top_score",
              customName: "kb_relevance_score",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI draft grounded response
      aiNode(
        "4",
        "AI draft grounded response",
        760,
        "You are a helpful customer support agent. Draft a response to this ticket using ONLY the knowledge base articles provided. The response must: address the customer by name, directly answer their question, include step-by-step instructions if applicable, link to relevant KB articles, match tone to sentiment (empathetic for frustrated, warm for neutral). If the KB does not contain the answer, say so clearly. Rate your confidence 0-100. Respond JSON: {draft_response: string, confidence: number, kb_articles_used: string, requires_escalation: boolean, suggested_tags: string}",
        "Ticket: {ticket_text}, Customer: {customer_name}, Intent: {intent}, Urgency: {urgency}, Sentiment: {sentiment}, KB articles: {kb_articles}",
        [
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "kb_articles_used", as: "kb_articles_used", type: "string" },
          {
            field: "requires_escalation",
            as: "requires_escalation",
            type: "string",
          },
          { field: "suggested_tags", as: "suggested_tags", type: "string" },
        ],
      ),

      // 5. Confidence condition
      n("5", "condition", "Confidence >= 85?", 1000, {
        conditionData: [
          {
            variable: "confidence",
            value: "85",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. High confidence: auto-send / Low: agent review
      n("6", "hitApi", "Auto-send response via Zendesk API", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"comment":{"body":"{draft_response}","public":true},"tags":[{suggested_tags}]}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "updated_ticket_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Outcome logging + low-confidence agent review
      slackNode(
        "7",
        "Notify support: ticket needs review or log outcome",
        1480,
        "Ticket #{ticket_id} — {intent} ({urgency})\\nCustomer: {customer_name} ({sentiment})\\nConfidence: {confidence}%\\nDraft: {draft_response}\\nKB used: {kb_articles_used}\\n\\nAction: Review and send/edit response",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high confidence -> auto-send
      e("5", "7", "output_2"), // low confidence -> agent review
      e("6", "7"),
    ],
    summary: `**AI Customer Response** — Classifies tickets, retrieves KB articles, and drafts grounded responses with confidence routing.\n\n1. Support ticket webhook fires from Zendesk/Intercom\n2. **AI classifies** intent, urgency, and sentiment from the ticket\n3. **Knowledge base search** retrieves relevant articles\n4. **AI drafts** a grounded response using only KB content\n5. Confidence gate routes high vs. low confidence\n6. High confidence: **auto-sent** via Zendesk API\n7. Low confidence: **Slack alert** for agent review with draft\n\n**To activate:** Say "connect openai", "connect zendesk", "connect your KB API", "connect slack"`,
  },

  // ===================================================================
  // 59. AI DASHBOARD NARRATIVE (8 nodes)
  // ===================================================================

  "AI Dashboard Narrative": {
    nodes: [
      trigger("Schedule: Daily dashboard narrative", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs daily to pull KPIs, detect anomalies, and generate an executive narrative of business performance",
        setup:
          "Connect your data warehouse or BI tool API. The workflow pulls KPIs, computes deltas, and generates a natural-language report.",
        inputFields: [],
      }),

      // 2. Pull KPIs from data warehouse
      n("2", "hitApi", "Pull KPIs from BigQuery / data warehouse", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://bigquery.googleapis.com/bigquery/v2/projects/{project_id}/queries",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT metric_name, current_value, previous_value, target_value FROM kpi_dashboard WHERE report_date = CURRENT_DATE() ORDER BY metric_name",
            },
            { key: "useLegacySql", value: "false" },
          ],
          variables: [
            { name: "rows", customName: "kpi_data", type: "string" },
            { name: "totalRows", customName: "total_kpis", type: "number" },
          ],
        },
      }),

      // 3. AI compute period deltas and anomaly detection
      aiNode(
        "3",
        "AI compute deltas & detect anomalies",
        520,
        "You are a data analyst. Analyze these KPI values and compute: period-over-period delta (% change), distance from target, and anomaly flags. An anomaly is any metric that changed more than 2 standard deviations from its 30-day average, or missed target by more than 20%. Respond JSON: {kpi_deltas: string, anomaly_count: number, anomalies: string, biggest_win: string, biggest_concern: string, overall_health: string}",
        "KPI data: {kpi_data}, Total KPIs: {total_kpis}",
        [
          { field: "kpi_deltas", as: "kpi_deltas", type: "string" },
          { field: "anomaly_count", as: "anomaly_count", type: "number" },
          { field: "anomalies", as: "anomalies", type: "string" },
          { field: "biggest_win", as: "biggest_win", type: "string" },
          { field: "biggest_concern", as: "biggest_concern", type: "string" },
          { field: "overall_health", as: "overall_health", type: "string" },
        ],
      ),

      // 4. AI driver analysis
      aiNode(
        "4",
        "AI analyze root causes & drivers",
        760,
        "You are a business analyst. For each anomaly and significant change, hypothesize the likely driver: seasonal pattern, campaign effect, product change, market shift, or data quality issue. Provide confidence in each hypothesis. For the biggest concern, suggest 3 immediate actions. Respond JSON: {driver_analysis: string, action_items: string, hypothesis_confidence: string, narrative_headline: string, executive_summary: string}",
        "KPI deltas: {kpi_deltas}, Anomalies: {anomalies}, Biggest win: {biggest_win}, Biggest concern: {biggest_concern}",
        [
          { field: "driver_analysis", as: "driver_analysis", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          {
            field: "hypothesis_confidence",
            as: "hypothesis_confidence",
            type: "string",
          },
          {
            field: "narrative_headline",
            as: "narrative_headline",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 5. Negative change condition
      n("5", "condition", "Has anomalies requiring fast alert?", 1000, {
        conditionData: [
          {
            variable: "anomaly_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Fast alert for anomalies
      slackNode(
        "6",
        "Fast alert: KPI anomalies detected",
        1240,
        "KPI ANOMALY ALERT\\n{anomaly_count} anomalies detected\\n\\nBiggest concern: {biggest_concern}\\nOverall health: {overall_health}\\n\\nAnomalies:\\n{anomalies}\\n\\nImmediate actions:\\n{action_items}",
        460,
      ),

      // 7. Report assembly
      aiNode(
        "7",
        "AI assemble executive report",
        1480,
        "You are a business writer. Assemble a polished executive dashboard narrative that reads like a morning briefing. Lead with the headline, then cover: overall health status, top wins, key concerns with drivers, anomaly explanations, and recommended actions. Keep it under 500 words, use bullet points for metrics, and bold the most important numbers. Respond JSON: {full_report: string, report_word_count: number, report_date: string}",
        "Headline: {narrative_headline}, Executive summary: {executive_summary}, KPI deltas: {kpi_deltas}, Driver analysis: {driver_analysis}, Actions: {action_items}, Health: {overall_health}",
        [
          { field: "full_report", as: "full_report", type: "string" },
          {
            field: "report_word_count",
            as: "report_word_count",
            type: "number",
          },
          { field: "report_date", as: "report_date", type: "string" },
        ],
      ),

      // 8. Distribution + archive
      emailNode(
        "8",
        "Email: Daily dashboard narrative",
        1720,
        "{exec_distribution_list}",
        "Dashboard: {narrative_headline} — {report_date}",
        "{full_report}\\n\\n---\\nAnomalies: {anomaly_count} | Overall: {overall_health}\\nBiggest win: {biggest_win}\\nBiggest concern: {biggest_concern}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // anomalies -> fast alert
      e("5", "7", "output_2"), // no anomalies -> regular report
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Dashboard Narrative** — Turns raw KPIs into a daily executive briefing with anomaly detection.\n\n1. Runs every morning on schedule\n2. **BigQuery** pulls current KPI values, previous values, and targets\n3. **AI computes** period deltas and detects statistical anomalies\n4. **AI analyzes** root cause drivers and suggests immediate actions\n5. Anomaly gate triggers fast alerts when needed\n6. **Slack fast alert** for significant anomalies with action items\n7. **AI assembles** polished executive narrative report\n8. **Email distributed** to executive team with full briefing\n\n**To activate:** Say "connect openai", "connect bigquery", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 60. AI DATA CLASSIFIER (7 nodes)
  // ===================================================================

  "AI Data Classifier": {
    nodes: [
      trigger("Webhook: Batch records for classification", "webhook_app", {
        source: "ETL Pipeline / CSV Upload / API",
        event: "batch.submitted",
        howItStarts:
          "A batch of records is submitted for AI-powered classification — leads, tickets, products, or documents",
        setup:
          "Configure your ETL pipeline or data ingestion tool to POST batches of records to this workflow URL. Include records as a JSON array with id and text fields.",
        inputFields: [],
      }),

      // 2. Clean and normalize
      aiNode(
        "2",
        "AI clean & normalize batch records",
        280,
        "Read this batch submission payload and extract the records regardless of format. Clean the data: trim whitespace, normalize casing, remove duplicates, standardize date formats, and flag records with missing required fields. Respond JSON: {cleaned_records: string, total_records: number, duplicates_removed: number, incomplete_records: number, record_format: string}",
        "{webhook_payload}",
        [
          { field: "cleaned_records", as: "cleaned_records", type: "string" },
          { field: "total_records", as: "total_records", type: "number" },
          {
            field: "duplicates_removed",
            as: "duplicates_removed",
            type: "number",
          },
          {
            field: "incomplete_records",
            as: "incomplete_records",
            type: "number",
          },
          { field: "record_format", as: "record_format", type: "string" },
        ],
      ),

      // 3. AI classification with confidence
      aiNode(
        "3",
        "AI classify records with confidence scores",
        520,
        "You are a data classification expert. Classify each record into the appropriate category. For each record, provide: the assigned category, a confidence score 0-100, the reasoning, and secondary category if confidence is below 80. Track the overall distribution of categories. Respond JSON: {classified_records: string, category_distribution: string, avg_confidence: number, low_confidence_count: number, high_confidence_count: number, categories_used: string}",
        "Records: {cleaned_records}, Format: {record_format}",
        [
          {
            field: "classified_records",
            as: "classified_records",
            type: "string",
          },
          {
            field: "category_distribution",
            as: "category_distribution",
            type: "string",
          },
          { field: "avg_confidence", as: "avg_confidence", type: "number" },
          {
            field: "low_confidence_count",
            as: "low_confidence_count",
            type: "number",
          },
          {
            field: "high_confidence_count",
            as: "high_confidence_count",
            type: "number",
          },
          { field: "categories_used", as: "categories_used", type: "string" },
        ],
      ),

      // 4. Low confidence condition
      n("4", "condition", "Has low-confidence records (< 80)?", 760, {
        conditionData: [
          {
            variable: "low_confidence_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Manual review queue for low confidence / auto-write for high
      n(
        "5",
        "hitApi",
        "Push low-confidence records to review queue",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.your-company.com/classification/review-queue",
            headers: [
              { key: "Authorization", value: "Bearer {internal_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "records", value: "{classified_records}" },
              { key: "filter", value: "low_confidence" },
              { key: "batch_id", value: "{batch_id}" },
            ],
            variables: [
              {
                name: "queue_id",
                customName: "review_queue_id",
                type: "string",
              },
              {
                name: "queued_count",
                customName: "queued_for_review",
                type: "number",
              },
            ],
          },
        },
        460,
      ),

      // 6. Distribution summary and auto-write
      n(
        "6",
        "hitApi",
        "Write high-confidence classifications to database",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.your-company.com/classification/batch-write",
            headers: [
              { key: "Authorization", value: "Bearer {internal_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "records", value: "{classified_records}" },
              { key: "filter", value: "high_confidence" },
              { key: "batch_id", value: "{batch_id}" },
            ],
            variables: [
              {
                name: "written_count",
                customName: "auto_written",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. Correction feedback loop + summary
      emailNode(
        "7",
        "Email: Classification batch summary",
        1240,
        "{data_team_email}",
        "Data Classification Complete — {total_records} records processed",
        "Classification Batch Summary\\n\\nTotal records: {total_records}\\nDuplicates removed: {duplicates_removed}\\nIncomplete: {incomplete_records}\\n\\nClassification Results:\\nHigh confidence (auto-written): {high_confidence_count}\\nLow confidence (queued for review): {low_confidence_count}\\nAverage confidence: {avg_confidence}%\\n\\nCategory Distribution:\\n{category_distribution}\\n\\nCategories used: {categories_used}\\n\\nPlease review the low-confidence queue and provide corrections to improve future accuracy.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // low confidence exists -> review queue
      e("4", "6", "output_2"), // all high confidence -> auto-write
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Data Classifier** — Batch classification with confidence-based routing to auto-write or manual review.\n\n1. Batch records submitted via webhook from ETL pipeline\n2. **AI cleans** and normalizes records, removes duplicates\n3. **AI classifies** each record with category and confidence score\n4. Confidence gate splits low vs. high confidence\n5. Low-confidence: routed to **manual review queue**\n6. High-confidence: **auto-written** to database\n7. **Email summary** with distribution stats and review queue link\n\n**To activate:** Say "connect openai", "connect your data API", "connect email"`,
  },

  // ===================================================================
  // 61. AI DOCUMENT ANALYZER (8 nodes)
  // ===================================================================

  "AI Document Analyzer": {
    nodes: [
      trigger("Webhook: Document submitted for analysis", "webhook_app", {
        source: "SharePoint / Google Drive / Email",
        event: "document.uploaded",
        howItStarts:
          "A document is uploaded for AI analysis — contracts, reports, invoices, or policies",
        setup:
          "Configure SharePoint or Google Drive to POST documents to this workflow URL on upload. Or forward emails with attachments to the workflow trigger address.",
        inputFields: [],
      }),

      // 2. Extract text and detect document type
      aiNode(
        "2",
        "AI extract text & detect document type",
        280,
        "Read this document submission payload and extract all content regardless of format. Identify the document type (contract/invoice/report/policy/proposal/resume), extract the full text, detect the language, and note any tables or structured data present. Respond JSON: {document_text: string, document_type: string, language: string, has_tables: boolean, page_count_estimate: number, requester_email: string, analysis_lens: string}",
        "{webhook_payload}",
        [
          { field: "document_text", as: "document_text", type: "string" },
          { field: "document_type", as: "document_type", type: "string" },
          { field: "language", as: "language", type: "string" },
          { field: "has_tables", as: "has_tables", type: "string" },
          {
            field: "page_count_estimate",
            as: "page_count_estimate",
            type: "number",
          },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "analysis_lens", as: "analysis_lens", type: "string" },
        ],
      ),

      // 3. AI entity and obligation extraction
      aiNode(
        "3",
        "AI extract entities, obligations & key data",
        520,
        "You are a document analysis expert. Extract all important entities, obligations, dates, financial figures, and key data points from this document. Adapt your extraction to the document type: for contracts find parties/terms/obligations, for invoices find line items/totals/due dates, for reports find findings/recommendations, for policies find rules/requirements. Respond JSON: {entities: string, obligations: string, key_dates: string, financial_figures: string, key_data_points: string, entity_count: number, obligation_count: number}",
        "Document ({document_type}): {document_text}, Analysis lens: {analysis_lens}",
        [
          { field: "entities", as: "entities", type: "string" },
          { field: "obligations", as: "obligations", type: "string" },
          { field: "key_dates", as: "key_dates", type: "string" },
          {
            field: "financial_figures",
            as: "financial_figures",
            type: "string",
          },
          { field: "key_data_points", as: "key_data_points", type: "string" },
          { field: "entity_count", as: "entity_count", type: "number" },
          { field: "obligation_count", as: "obligation_count", type: "number" },
        ],
      ),

      // 4. AI executive summary
      aiNode(
        "4",
        "AI generate executive summary",
        760,
        "You are an executive assistant. Write a concise executive summary of this document (under 300 words). Highlight: the document purpose, key findings/terms, critical dates, financial impact, and recommended actions. Tailor the summary to the document type and analysis lens. Respond JSON: {executive_summary: string, risk_flags: string, action_required: boolean, recommended_actions: string, priority_level: string}",
        "Type: {document_type}, Entities: {entities}, Obligations: {obligations}, Key dates: {key_dates}, Financials: {financial_figures}, Key data: {key_data_points}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "risk_flags", as: "risk_flags", type: "string" },
          { field: "action_required", as: "action_required", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "priority_level", as: "priority_level", type: "string" },
        ],
      ),

      // 5. Action condition
      n("5", "condition", "Action required from document?", 1000, {
        conditionData: [
          {
            variable: "action_required",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Create tasks from document analysis
      n("6", "hitApi", "Create action tasks in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Action Required: {document_type} analysis","notes":"Priority: {priority_level}\\n\\nSummary:\\n{executive_summary}\\n\\nRecommended Actions:\\n{recommended_actions}\\n\\nKey Dates:\\n{key_dates}\\n\\nRisk Flags:\\n{risk_flags}","projects":["{doc_review_project_id}"]}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "task_id", type: "string" },
          ],
        },
      }),

      // 7. Notify requester with no-action summary
      n(
        "7",
        "hitApi",
        "Log analysis to document archive",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.your-company.com/documents/analysis-log",
            headers: [
              { key: "Authorization", value: "Bearer {internal_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "document_type", value: "{document_type}" },
              { key: "entities", value: "{entities}" },
              { key: "obligations", value: "{obligations}" },
              { key: "summary", value: "{executive_summary}" },
              { key: "priority", value: "{priority_level}" },
            ],
            variables: [
              { name: "id", customName: "archive_log_id", type: "string" },
            ],
          },
        },
        460,
      ),

      // 8. Requester notification
      emailNode(
        "8",
        "Email requester: analysis complete",
        1480,
        "{requester_email}",
        "Document Analysis: {document_type} — {priority_level} priority",
        "Document Analysis Complete\\n\\nType: {document_type} | Language: {language}\\nPages: ~{page_count_estimate} | Entities: {entity_count} | Obligations: {obligation_count}\\n\\nExecutive Summary:\\n{executive_summary}\\n\\nKey Dates:\\n{key_dates}\\n\\nFinancial Figures:\\n{financial_figures}\\n\\nRisk Flags:\\n{risk_flags}\\n\\nRecommended Actions:\\n{recommended_actions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // action required -> create tasks
      e("5", "7", "output_2"), // no action -> archive only
      e("6", "8"),
      e("7", "8"),
    ],
    summary: `**AI Document Analyzer** — Extracts entities, obligations, and key data from any document type with action routing.\n\n1. Document uploaded via webhook from SharePoint/Drive/email\n2. **AI extracts** text, detects document type and language\n3. **AI extracts** entities, obligations, dates, and financial figures\n4. **AI generates** executive summary with risk flags and actions\n5. Action gate routes actionable vs. informational documents\n6. Action required: **Asana tasks** created with recommendations\n7. All analyses **archived** for reference\n8. **Email notification** to requester with full analysis\n\n**To activate:** Say "connect openai", "connect asana", "connect email"`,
  },

  // ===================================================================
  // 62. AI EMAIL DRAFTING BOT (8 nodes)
  // ===================================================================

  "AI Email Drafting Bot": {
    nodes: [
      trigger("Webhook: Email draft request submitted", "webhook_app", {
        source: "Slack Command / Internal Tool / CRM",
        event: "draft.requested",
        howItStarts:
          "A team member requests an AI-drafted email with recipient, context, and desired outcome",
        setup:
          "Configure a Slack slash command (/draft-email) or internal form to POST draft requests to this workflow URL. Include recipient email, context, goal, and tone preferences.",
        inputFields: [],
      }),

      // 2. Enrich recipient from CRM
      aiNode(
        "2",
        "AI extract draft request details",
        280,
        "Read this email draft request payload and extract all details regardless of format. Find the recipient email, subject context, desired outcome, tone preference, requester email, and any attachments or data to reference. Respond JSON: {recipient_email: string, subject_context: string, desired_outcome: string, tone: string, requester_email: string, reference_data: string, urgency: string}",
        "{webhook_payload}",
        [
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "subject_context", as: "subject_context", type: "string" },
          { field: "desired_outcome", as: "desired_outcome", type: "string" },
          { field: "tone", as: "tone", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "reference_data", as: "reference_data", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. CRM recipient enrichment
      n("3", "hitApi", "Enrich recipient from HubSpot CRM", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/{recipient_email}?idProperty=email&properties=firstname,lastname,company,jobtitle,lifecyclestage,recent_deal_amount",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "properties.firstname",
              customName: "recipient_first_name",
              type: "string",
            },
            {
              name: "properties.lastname",
              customName: "recipient_last_name",
              type: "string",
            },
            {
              name: "properties.company",
              customName: "recipient_company",
              type: "string",
            },
            {
              name: "properties.jobtitle",
              customName: "recipient_title",
              type: "string",
            },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI build context pack and draft email
      aiNode(
        "4",
        "AI draft email with subject lines",
        760,
        "You are a professional email writer. Draft an email using the recipient context and desired outcome. Include: 3 subject line options (A/B/C), a personalized greeting using their name and company, body that achieves the desired outcome, and a clear CTA. Match the specified tone. Flag if the email discusses pricing, legal terms, HR matters, or confidential information. Respond JSON: {subject_a: string, subject_b: string, subject_c: string, email_body: string, is_sensitive: boolean, sensitivity_reason: string, word_count: number, readability_grade: string}",
        "Recipient: {recipient_first_name} {recipient_last_name}, Title: {recipient_title}, Company: {recipient_company}, Stage: {lifecycle_stage}, Context: {subject_context}, Goal: {desired_outcome}, Tone: {tone}, Reference: {reference_data}",
        [
          { field: "subject_a", as: "subject_a", type: "string" },
          { field: "subject_b", as: "subject_b", type: "string" },
          { field: "subject_c", as: "subject_c", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "string" },
          {
            field: "sensitivity_reason",
            as: "sensitivity_reason",
            type: "string",
          },
          { field: "word_count", as: "word_count", type: "number" },
          {
            field: "readability_grade",
            as: "readability_grade",
            type: "string",
          },
        ],
      ),

      // 5. Sensitivity condition
      n("5", "condition", "Is email content sensitive?", 1000, {
        conditionData: [
          {
            variable: "is_sensitive",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. HITL for sensitive emails
      n(
        "6",
        "approvalGate",
        "Approval: sensitive email requires review",
        1240,
        {
          approvalGateData: {
            message:
              "SENSITIVE EMAIL DRAFT — requires human review before sending\\n\\nRecipient: {recipient_first_name} {recipient_last_name} ({recipient_company})\\nSensitivity: {sensitivity_reason}\\n\\nSubject options:\\nA: {subject_a}\\nB: {subject_b}\\nC: {subject_c}\\n\\nBody:\\n{email_body}\\n\\nApprove to create Gmail draft, or reject to discard.",
          },
        },
        460,
      ),

      // 7. Create Gmail draft
      n("7", "hitApi", "Create Gmail draft", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://gmail.googleapis.com/gmail/v1/users/me/drafts",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message", value: '{"raw":"' },
            { key: "to", value: "{recipient_email}" },
            { key: "subject", value: "{subject_a}" },
            { key: "body", value: "{email_body}" },
          ],
          variables: [{ name: "id", customName: "draft_id", type: "string" }],
        },
      }),

      // 8. Notify requester
      slackNode(
        "8",
        "Notify requester: draft ready in Gmail",
        1720,
        "Email draft created for *{recipient_first_name} {recipient_last_name}* ({recipient_company})\\nSubject: {subject_a}\\nWord count: {word_count} | Readability: {readability_grade}\\n\\nAlt subjects:\\nB: {subject_b}\\nC: {subject_c}\\n\\nDraft is in your Gmail — review and send!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // sensitive -> approval gate
      e("5", "7", "output_2"), // not sensitive -> auto-create draft
      e("6", "7"), // approved -> create draft
      e("7", "8"),
    ],
    summary: `**AI Email Drafting Bot** — CRM-enriched email drafting with sensitivity detection and approval routing.\n\n1. Draft request submitted via Slack command or webhook\n2. **AI extracts** recipient, context, desired outcome, and tone\n3. **HubSpot CRM** enriches recipient with name, company, and stage\n4. **AI drafts** email with 3 subject lines, personalized body, and CTA\n5. Sensitivity gate detects pricing, legal, HR, or confidential content\n6. Sensitive emails routed to **approval gate** for human review\n7. **Gmail API** creates draft in requester's inbox\n8. **Slack notification** with draft details and alternative subjects\n\n**To activate:** Say "connect openai", "connect hubspot", "connect gmail", "connect slack"`,
  },

  // ===================================================================
  // 63. AI EMAIL RESPONDER (7 nodes)
  // ===================================================================

  "AI Email Responder": {
    nodes: [
      trigger("Webhook: Inbound email to support", "email", {
        source: "Email / Gmail / Outlook",
        event: "email.received",
        howItStarts:
          "An inbound email is received at your support address (support@, help@, info@)",
        setup:
          "Configure email forwarding rules to POST inbound emails to this workflow URL. Or use Gmail/Outlook API push notifications for new messages in the support inbox.",
        inputFields: [],
      }),

      // 2. KB retrieval
      n("2", "hitApi", "Search knowledge base for email topic", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/kb/search",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query", value: "{webhook_payload}" },
            { key: "limit", value: "5" },
          ],
          variables: [
            { name: "results", customName: "kb_articles", type: "string" },
            {
              name: "top_score",
              customName: "kb_relevance_score",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI draft response with steps
      aiNode(
        "3",
        "AI draft email response with step-by-step instructions",
        520,
        "You are a professional support agent. Read this inbound email and draft a comprehensive response. Use ONLY the knowledge base articles to ground your answer. Include: greeting by name if available, direct answer to the question, step-by-step instructions if applicable, links to relevant KB articles, and a warm closing. Rate your confidence 0-100. Respond JSON: {sender_email: string, sender_name: string, original_subject: string, draft_response: string, confidence: number, kb_articles_used: string, needs_human: boolean, category: string}",
        "Inbound email: {webhook_payload}, KB articles: {kb_articles}",
        [
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "original_subject", as: "original_subject", type: "string" },
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "kb_articles_used", as: "kb_articles_used", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "category", as: "category", type: "string" },
        ],
      ),

      // 4. Quality score condition
      n("4", "condition", "Confidence >= 90 and KB relevant?", 760, {
        conditionData: [
          {
            variable: "confidence",
            value: "90",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Auto-send high confidence
      emailNode(
        "5",
        "Auto-send response to sender",
        1000,
        "{sender_email}",
        "Re: {original_subject}",
        "{draft_response}",
      ),

      // 6. Low confidence: support team review
      slackNode(
        "6",
        "Alert support: email needs human review",
        1000,
        "Email response needs review\\nFrom: {sender_name} ({sender_email})\\nSubject: {original_subject}\\nCategory: {category}\\nConfidence: {confidence}%\\n\\nDraft response:\\n{draft_response}\\n\\nKB articles used: {kb_articles_used}",
        460,
      ),

      // 7. Satisfaction signal capture
      n("7", "hitApi", "Log interaction for satisfaction tracking", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/support/interactions",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "sender_email", value: "{sender_email}" },
            { key: "category", value: "{category}" },
            { key: "confidence", value: "{confidence}" },
            { key: "auto_sent", value: "{confidence >= 90}" },
            { key: "kb_articles", value: "{kb_articles_used}" },
            { key: "subject", value: "{original_subject}" },
          ],
          variables: [
            { name: "id", customName: "interaction_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> auto-send
      e("4", "6", "output_2"), // low confidence -> human review
      e("5", "7"),
      e("6", "7"),
    ],
    summary: `**AI Email Responder** — Automatically responds to inbound support emails using KB-grounded answers.\n\n1. Inbound email detected via webhook\n2. **Knowledge base search** finds relevant articles\n3. **AI drafts** response with step-by-step instructions grounded in KB\n4. Confidence gate routes auto-send vs. human review\n5. High confidence: **auto-sent** to sender\n6. Low confidence: **Slack alert** for support team review\n7. All interactions **logged** for satisfaction tracking\n\n**To activate:** Say "connect openai", "connect your KB API", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 64. AI FINANCIAL REPORT GENERATOR (9 nodes)
  // ===================================================================

  "AI Financial Report Generator": {
    nodes: [
      trigger("Schedule: Monthly financial report", "schedule", {
        schedule: "First business day of each month at 9:00 AM",
        howItStarts:
          "Runs monthly to pull accounting data, compute variances, and generate a CFO-ready financial report",
        setup:
          "Connect your accounting system (QuickBooks/Xero/NetSuite) and data warehouse. The workflow pulls financial data and generates narrative reports.",
        inputFields: [],
      }),

      // 2. Pull accounting data
      n("2", "hitApi", "Pull financial data from QuickBooks API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://quickbooks.api.intuit.com/v3/company/{company_id}/reports/ProfitAndLoss?start_date={month_start}&end_date={month_end}&summarize_column_by=Month",
          headers: [
            { key: "Authorization", value: "Bearer {quickbooks_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "Rows", customName: "pnl_data", type: "string" },
            {
              name: "Header.ReportName",
              customName: "report_name",
              type: "string",
            },
            { name: "Header.DateMacro", customName: "period", type: "string" },
          ],
        },
      }),

      // 3. AI compute variances
      aiNode(
        "3",
        "AI compute variances & budget comparison",
        520,
        "You are a financial analyst. Analyze this P&L data and compute: revenue variance vs budget, expense variance by category, gross margin change, operating margin change, and YoY comparison. Identify the top 3 positive variances and top 3 negative variances. Respond JSON: {revenue_actual: number, revenue_budget: number, revenue_variance_pct: number, expense_variances: string, gross_margin_pct: number, operating_margin_pct: number, top_positive_variances: string, top_negative_variances: string, total_revenue: number, total_expenses: number}",
        "P&L data: {pnl_data}, Period: {period}",
        [
          { field: "revenue_actual", as: "revenue_actual", type: "number" },
          { field: "revenue_budget", as: "revenue_budget", type: "number" },
          {
            field: "revenue_variance_pct",
            as: "revenue_variance_pct",
            type: "number",
          },
          {
            field: "expense_variances",
            as: "expense_variances",
            type: "string",
          },
          { field: "gross_margin_pct", as: "gross_margin_pct", type: "number" },
          {
            field: "operating_margin_pct",
            as: "operating_margin_pct",
            type: "number",
          },
          {
            field: "top_positive_variances",
            as: "top_positive_variances",
            type: "string",
          },
          {
            field: "top_negative_variances",
            as: "top_negative_variances",
            type: "string",
          },
          { field: "total_revenue", as: "total_revenue", type: "number" },
          { field: "total_expenses", as: "total_expenses", type: "number" },
        ],
      ),

      // 4. Reconciliation gap detection
      aiNode(
        "4",
        "AI detect reconciliation gaps & anomalies",
        760,
        "You are an auditor. Examine these financial figures for reconciliation gaps: unmatched transactions, rounding discrepancies, category misallocations, and unusual patterns (e.g., expense spikes, revenue dips). Flag anything that needs investigation before board presentation. Respond JSON: {reconciliation_gaps: string, gap_count: number, anomaly_flags: string, data_quality_score: number, investigation_needed: string}",
        "P&L: {pnl_data}, Revenue: {total_revenue}, Expenses: {total_expenses}, Variances: {expense_variances}",
        [
          {
            field: "reconciliation_gaps",
            as: "reconciliation_gaps",
            type: "string",
          },
          { field: "gap_count", as: "gap_count", type: "number" },
          { field: "anomaly_flags", as: "anomaly_flags", type: "string" },
          {
            field: "data_quality_score",
            as: "data_quality_score",
            type: "number",
          },
          {
            field: "investigation_needed",
            as: "investigation_needed",
            type: "string",
          },
        ],
      ),

      // 5. AI CFO narrative
      aiNode(
        "5",
        "AI generate CFO narrative report",
        1000,
        "You are a CFO writing a monthly financial narrative for the board. Write a professional report covering: headline performance summary, revenue analysis with drivers, expense analysis with major categories, margin trends, key variances (positive and negative), reconciliation notes, and forward-looking commentary. Use specific numbers. Keep under 800 words. Respond JSON: {cfo_narrative: string, report_headline: string, key_takeaway: string, forward_outlook: string}",
        "Revenue: ${total_revenue} (budget: ${revenue_budget}, variance: {revenue_variance_pct}%), Gross margin: {gross_margin_pct}%, Operating margin: {operating_margin_pct}%, Positive variances: {top_positive_variances}, Negative variances: {top_negative_variances}, Reconciliation: {reconciliation_gaps}, Anomalies: {anomaly_flags}",
        [
          { field: "cfo_narrative", as: "cfo_narrative", type: "string" },
          { field: "report_headline", as: "report_headline", type: "string" },
          { field: "key_takeaway", as: "key_takeaway", type: "string" },
          { field: "forward_outlook", as: "forward_outlook", type: "string" },
        ],
      ),

      // 6. Deviation condition
      n("6", "condition", "Revenue variance exceeds -10%?", 1240, {
        conditionData: [
          {
            variable: "revenue_variance_pct",
            value: "-10",
            comparison_operator: "LESS THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Finance alert for major deviations
      slackNode(
        "7",
        "Alert finance: major revenue deviation",
        1480,
        "FINANCE ALERT — Revenue deviation exceeds threshold\\nRevenue variance: {revenue_variance_pct}%\\nActual: ${revenue_actual} vs Budget: ${revenue_budget}\\n\\nKey takeaway: {key_takeaway}\\nReconciliation gaps: {gap_count}\\n\\nInvestigation needed:\\n{investigation_needed}",
        460,
      ),

      // 8. Build and publish report
      n("8", "hitApi", "Publish report to Google Docs", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Financial Report — {period}" },
            {
              key: "body",
              value:
                '{"content":[{"paragraph":{"elements":[{"textRun":{"content":"{cfo_narrative}"}}]}}]}',
            },
          ],
          variables: [
            { name: "documentId", customName: "report_doc_id", type: "string" },
          ],
        },
      }),

      // 9. Source verification + archive
      emailNode(
        "9",
        "Email: Monthly financial report",
        1960,
        "{finance_team_email}",
        "Financial Report: {report_headline} — {period}",
        "{cfo_narrative}\\n\\n---\\nData quality score: {data_quality_score}/100\\nReconciliation gaps: {gap_count}\\nReport: https://docs.google.com/document/d/{report_doc_id}\\n\\nForward outlook: {forward_outlook}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // major deviation -> finance alert
      e("6", "8", "output_2"), // within tolerance -> publish report
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Financial Report Generator** — Monthly P&L analysis with variance detection and CFO-ready narrative.\n\n1. Runs first business day of each month\n2. **QuickBooks API** pulls P&L and financial data\n3. **AI computes** variances, budget comparisons, and margin trends\n4. **AI detects** reconciliation gaps and anomalies\n5. **AI writes** CFO-ready narrative report with specific numbers\n6. Revenue deviation gate triggers alerts for major misses\n7. **Slack alert** for significant revenue deviations\n8. Report **published** to Google Docs\n9. **Email distributed** to finance team with data quality score\n\n**To activate:** Say "connect openai", "connect quickbooks", "connect google docs", "connect slack"`,
  },

  // ===================================================================
  // 65. AI FITNESS COACHING BOT (8 nodes)
  // ===================================================================

  "AI Fitness Coaching Bot": {
    nodes: [
      trigger("Webhook: User fitness message received", "webhook_app", {
        source: "Chat Widget / Mobile App / WhatsApp",
        event: "message.received",
        howItStarts:
          "A user sends a message to the fitness coaching bot asking for workout advice, nutrition guidance, or progress tracking",
        setup:
          "Configure your chat widget or messaging platform to POST user messages to this workflow URL. Include user ID, message text, and conversation history.",
        inputFields: [],
      }),

      // 2. Intent classification
      aiNode(
        "2",
        "AI classify fitness intent",
        280,
        "Read this user message and classify the intent. Determine if this is: workout_request (asking for exercises/routines), nutrition_question (asking about diet/calories/macros), progress_check (reporting results or asking about progress), injury_concern (mentioning pain/discomfort/injury), motivation (feeling discouraged or asking for encouragement), or general_chat. Also extract user_id and any health metrics mentioned. Respond JSON: {user_message: string, user_id: string, intent: string, health_metrics: string, is_safety_concern: boolean, safety_reason: string, user_name: string}",
        "{webhook_payload}",
        [
          { field: "user_message", as: "user_message", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "intent", as: "intent", type: "string" },
          { field: "health_metrics", as: "health_metrics", type: "string" },
          {
            field: "is_safety_concern",
            as: "is_safety_concern",
            type: "string",
          },
          { field: "safety_reason", as: "safety_reason", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
        ],
      ),

      // 3. Safety condition
      n("3", "condition", "Is there a safety/injury concern?", 520, {
        conditionData: [
          {
            variable: "is_safety_concern",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI personalized response
      aiNode(
        "4",
        "AI generate personalized coaching response",
        760,
        "You are a certified personal trainer and nutrition coach. Respond to this user message with personalized, evidence-based guidance. If workout_request: suggest specific exercises with sets/reps/rest. If nutrition_question: provide macro-based advice. If progress_check: analyze their metrics and encourage. If motivation: provide specific, actionable encouragement. Always be supportive and specific. NEVER diagnose medical conditions. Respond JSON: {coaching_response: string, response_type: string, metrics_to_track: string, follow_up_topic: string}",
        "User: {user_name}, Message: {user_message}, Intent: {intent}, Health metrics: {health_metrics}",
        [
          {
            field: "coaching_response",
            as: "coaching_response",
            type: "string",
          },
          { field: "response_type", as: "response_type", type: "string" },
          { field: "metrics_to_track", as: "metrics_to_track", type: "string" },
          { field: "follow_up_topic", as: "follow_up_topic", type: "string" },
        ],
      ),

      // 5. Append disclaimer
      n("5", "runCode", "Append health disclaimer to response", 1000, {
        runCodeData: {
          code: "const response = input.coaching_response; const disclaimer = '\\n\\n---\\nDisclaimer: This is AI-generated fitness guidance, not medical advice. Consult a healthcare professional before starting any new exercise or nutrition program.'; output.final_response = response + disclaimer; output.has_disclaimer = true;",
        },
      }),

      // 6. Log interaction
      n("6", "hitApi", "Log coaching interaction to database", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/fitness/interactions",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "user_id", value: "{user_id}" },
            { key: "intent", value: "{intent}" },
            { key: "message", value: "{user_message}" },
            { key: "response_type", value: "{response_type}" },
            { key: "health_metrics", value: "{health_metrics}" },
          ],
          variables: [
            { name: "id", customName: "interaction_id", type: "string" },
          ],
        },
      }),

      // 7. Schedule follow-up
      n("7", "delayAction", "Schedule follow-up check-in (48 hours)", 1480, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 8. Adherence tracking + follow-up message
      n("8", "sendMessage", "Send follow-up check-in message", 1720, {
        sendMessageData: {
          message:
            "Hey {user_name}! Just checking in on your {follow_up_topic}. How are you feeling? Any updates on your progress? Remember, consistency beats perfection!",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_2"), // no safety concern -> normal coaching
      e("3", "5", "output_1"), // safety concern -> add disclaimer immediately (skip coaching for injuries)
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Fitness Coaching Bot** — Personalized workout and nutrition guidance with safety routing and follow-ups.\n\n1. User message received via chat widget or messaging platform\n2. **AI classifies** intent: workout, nutrition, progress, injury, or motivation\n3. Safety gate routes injury concerns to immediate disclaimer\n4. **AI generates** personalized coaching response with specific guidance\n5. **Health disclaimer** appended to every response\n6. Interaction **logged** for analytics and adherence tracking\n7. **48-hour delay** for follow-up check-in\n8. **Follow-up message** sent to encourage consistency\n\n**To activate:** Say "connect openai", "connect your messaging platform"`,
  },

  // ===================================================================
  // 66. AI IMAGE GENERATOR (7 nodes)
  // ===================================================================

  "AI Image Generator": {
    nodes: [
      trigger("Webhook: Creative brief for image", "webhook_app", {
        source: "Slack / Design Tool / Marketing Portal",
        event: "brief.submitted",
        howItStarts:
          "A creative brief is submitted requesting AI-generated images for marketing, social media, or product assets",
        setup:
          "Configure a Slack slash command (/generate-image) or creative brief form to POST requests to this workflow URL. Include the visual description, style preferences, and intended use.",
        inputFields: [],
      }),

      // 2. AI rewrite prompt for optimal generation
      aiNode(
        "2",
        "AI rewrite prompt for optimal generation",
        280,
        "You are a prompt engineering expert for image generation. Take this creative brief and rewrite it as an optimized image generation prompt. Include: specific visual elements, art style (photorealistic/illustration/3d/flat), color palette, composition guidance, lighting, and negative prompts (what to avoid). Also determine the best aspect ratio and output size. Respond JSON: {optimized_prompt: string, negative_prompt: string, style: string, aspect_ratio: string, output_size: string, requester_email: string, use_case: string, original_brief: string}",
        "{webhook_payload}",
        [
          { field: "optimized_prompt", as: "optimized_prompt", type: "string" },
          { field: "negative_prompt", as: "negative_prompt", type: "string" },
          { field: "style", as: "style", type: "string" },
          { field: "aspect_ratio", as: "aspect_ratio", type: "string" },
          { field: "output_size", as: "output_size", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "original_brief", as: "original_brief", type: "string" },
        ],
      ),

      // 3. Generate image via DALL-E
      n("3", "hitApi", "Generate image via DALL-E 3 API", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            { key: "prompt", value: "{optimized_prompt}" },
            { key: "size", value: "{output_size}" },
            { key: "quality", value: "hd" },
            { key: "n", value: "1" },
          ],
          variables: [
            { name: "data[0].url", customName: "image_url", type: "string" },
            {
              name: "data[0].revised_prompt",
              customName: "revised_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 4. Style condition
      n("4", "condition", "Style matches brand guidelines?", 760, {
        conditionData: [
          {
            variable: "style",
            value: "off-brand",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Watermark and resize
      n(
        "5",
        "hitApi",
        "Process image: watermark + resize via Cloudinary",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.cloudinary.com/v1_1/{cloud_name}/image/upload",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "file", value: "{image_url}" },
              { key: "upload_preset", value: "{upload_preset}" },
              {
                key: "transformation",
                value: "w_1200,h_630,c_fill/l_watermark,o_30,g_south_east",
              },
              { key: "folder", value: "ai-generated/{use_case}" },
            ],
            variables: [
              {
                name: "secure_url",
                customName: "processed_image_url",
                type: "string",
              },
              {
                name: "public_id",
                customName: "image_public_id",
                type: "string",
              },
              { name: "bytes", customName: "file_size_bytes", type: "number" },
            ],
          },
        },
      ),

      // 6. Asset storage
      n("6", "hitApi", "Save asset metadata to DAM database", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/dam/assets",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "url", value: "{processed_image_url}" },
            { key: "original_brief", value: "{original_brief}" },
            { key: "prompt", value: "{optimized_prompt}" },
            { key: "style", value: "{style}" },
            { key: "use_case", value: "{use_case}" },
            { key: "size", value: "{output_size}" },
            { key: "file_size", value: "{file_size_bytes}" },
          ],
          variables: [{ name: "id", customName: "asset_id", type: "string" }],
        },
      }),

      // 7. Notify requester with thumbnails
      slackNode(
        "7",
        "Notify requester: image ready with preview",
        1480,
        "Image generated for: *{original_brief}*\\nStyle: {style} | Size: {output_size} | Use: {use_case}\\nFile size: {file_size_bytes} bytes\\n\\nImage: {processed_image_url}\\nAsset ID: {asset_id}\\n\\nPrompt used: {revised_prompt}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // on-brand -> process
      e("4", "2", "output_2"), // off-brand -> re-prompt
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Image Generator** — Optimized prompt engineering, DALL-E generation, and automated asset processing.\n\n1. Creative brief submitted via Slack or design portal\n2. **AI rewrites** brief into an optimized generation prompt with style and composition\n3. **DALL-E 3 API** generates high-quality image\n4. Style condition validates brand alignment\n5. **Cloudinary** processes image: watermark, resize, and optimize\n6. Asset metadata **saved** to Digital Asset Management database\n7. **Slack notification** with image preview and asset details\n\n**To activate:** Say "connect openai", "connect cloudinary", "connect slack"`,
  },

  // ===================================================================
  // 67. AI JOB DESCRIPTION GENERATOR (8 nodes)
  // ===================================================================

  "AI Job Description Generator": {
    nodes: [
      trigger("Webhook: New role requisition submitted", "webhook_app", {
        source: "HR Portal / Greenhouse / Lever / ATS",
        event: "requisition.created",
        howItStarts:
          "A hiring manager submits a new role requisition with basic requirements and team context",
        setup:
          "Configure your ATS (Greenhouse/Lever) or HR portal to POST requisition data to this workflow URL. Include role title, level, team, requirements, and hiring manager email.",
        inputFields: [],
      }),

      // 2. Pull culture and benefits info
      n("2", "hitApi", "Fetch company culture & benefits from HR API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-company.com/hr/culture-benefits?department={department}",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "culture.values",
              customName: "company_values",
              type: "string",
            },
            { name: "culture.perks", customName: "perks", type: "string" },
            {
              name: "benefits.summary",
              customName: "benefits_summary",
              type: "string",
            },
            {
              name: "culture.team_description",
              customName: "team_description",
              type: "string",
            },
          ],
        },
      }),

      // 3. Benchmark research
      aiNode(
        "3",
        "AI extract requisition & research benchmarks",
        520,
        "Read this role requisition payload and extract all details regardless of format. Then research market benchmarks: typical salary range for this role/level/location, common required skills, and competitive differentiators in job postings. Respond JSON: {role_title: string, level: string, department: string, location: string, requirements: string, team_size: string, hiring_manager_email: string, salary_benchmark: string, common_skills: string, competitive_factors: string}",
        "{webhook_payload}",
        [
          { field: "role_title", as: "role_title", type: "string" },
          { field: "level", as: "level", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "requirements", as: "requirements", type: "string" },
          { field: "team_size", as: "team_size", type: "string" },
          {
            field: "hiring_manager_email",
            as: "hiring_manager_email",
            type: "string",
          },
          { field: "salary_benchmark", as: "salary_benchmark", type: "string" },
          { field: "common_skills", as: "common_skills", type: "string" },
          {
            field: "competitive_factors",
            as: "competitive_factors",
            type: "string",
          },
        ],
      ),

      // 4. AI generate job description
      aiNode(
        "4",
        "AI generate comprehensive job description",
        760,
        "You are an expert talent acquisition writer. Generate a compelling, comprehensive job description. Include: engaging opening paragraph, about the role section, key responsibilities (6-8 bullets), required qualifications, preferred qualifications, about the team, benefits/perks highlights, and equal opportunity statement. Make it exciting but honest. Avoid jargon, gendered language, and unrealistic requirements. Respond JSON: {job_description: string, opening_paragraph: string, responsibilities: string, required_qualifications: string, preferred_qualifications: string, word_count: number}",
        "Role: {role_title}, Level: {level}, Department: {department}, Location: {location}, Requirements: {requirements}, Team: {team_description} ({team_size}), Culture: {company_values}, Benefits: {benefits_summary}, Perks: {perks}, Benchmarks: {salary_benchmark}, Common skills: {common_skills}",
        [
          { field: "job_description", as: "job_description", type: "string" },
          {
            field: "opening_paragraph",
            as: "opening_paragraph",
            type: "string",
          },
          { field: "responsibilities", as: "responsibilities", type: "string" },
          {
            field: "required_qualifications",
            as: "required_qualifications",
            type: "string",
          },
          {
            field: "preferred_qualifications",
            as: "preferred_qualifications",
            type: "string",
          },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 5. Inclusivity and bias pass
      aiNode(
        "5",
        "AI inclusivity & bias audit",
        1000,
        "You are a DEI and inclusive hiring expert. Audit this job description for: gendered language (he/she, guys, manpower), age bias (digital native, young, energetic), ability bias (stand, walk, lift without accommodation note), cultural bias (culture fit vs culture add), unnecessary requirements (degree when experience suffices, years of experience inflation), and exclusionary language. Score inclusivity 0-100 and rewrite any flagged sections. Respond JSON: {inclusivity_score: number, issues_found: string, issue_count: number, rewritten_sections: string, final_jd: string}",
        "Job description: {job_description}",
        [
          {
            field: "inclusivity_score",
            as: "inclusivity_score",
            type: "number",
          },
          { field: "issues_found", as: "issues_found", type: "string" },
          { field: "issue_count", as: "issue_count", type: "number" },
          {
            field: "rewritten_sections",
            as: "rewritten_sections",
            type: "string",
          },
          { field: "final_jd", as: "final_jd", type: "string" },
        ],
      ),

      // 6. HR approval gate
      n("6", "approvalGate", "HR approval: review job description", 1240, {
        approvalGateData: {
          message:
            "Job description ready for review\\n\\nRole: {role_title} ({level})\\nDepartment: {department} | Location: {location}\\nInclusivity score: {inclusivity_score}/100\\nIssues fixed: {issue_count}\\n\\n{final_jd}\\n\\nApprove to publish to ATS, or reject with feedback.",
        },
      }),

      // 7. Push to ATS
      n("7", "hitApi", "Create job posting in Greenhouse ATS", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://harvest.greenhouse.io/v1/jobs",
          headers: [
            { key: "Authorization", value: "Basic {greenhouse_token_b64}" },
            { key: "Content-Type", value: "application/json" },
            { key: "On-Behalf-Of", value: "{greenhouse_user_id}" },
          ],
          body: [
            { key: "name", value: "{role_title}" },
            { key: "department_id", value: "{department_id}" },
            { key: "notes", value: "{final_jd}" },
            { key: "status", value: "draft" },
          ],
          variables: [
            { name: "id", customName: "greenhouse_job_id", type: "string" },
          ],
        },
      }),

      // 8. Notify hiring manager
      emailNode(
        "8",
        "Email hiring manager: JD published",
        1720,
        "{hiring_manager_email}",
        "Job Description Published: {role_title} — Inclusivity {inclusivity_score}/100",
        "Hi,\\n\\nThe job description for *{role_title}* ({level}) has been approved and published to Greenhouse.\\n\\nInclusivity score: {inclusivity_score}/100\\nIssues resolved: {issue_count}\\nWord count: {word_count}\\n\\nGreenhouse Job ID: {greenhouse_job_id}\\n\\nNext steps: Review the posting in Greenhouse and activate when ready to accept applications.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Job Description Generator** — Inclusive, benchmark-informed job descriptions with HR approval workflow.\n\n1. Role requisition submitted via webhook from ATS or HR portal\n2. **HR API** fetches company culture, values, and benefits\n3. **AI extracts** role details and researches salary/skill benchmarks\n4. **AI writes** comprehensive JD with responsibilities, qualifications, and team info\n5. **AI inclusivity audit** checks for bias, gendered language, and unnecessary barriers\n6. **HR approval gate** for human review before publishing\n7. **Greenhouse ATS** receives the approved job posting\n8. **Email notification** to hiring manager with inclusivity score\n\n**To activate:** Say "connect openai", "connect your HR API", "connect greenhouse"`,
  },

  // ===================================================================
  // 68. AI LEGAL DOCUMENT ANALYZER (9 nodes)
  // ===================================================================

  "AI Legal Document Analyzer": {
    nodes: [
      trigger("Webhook: Legal document submitted", "webhook_app", {
        source: "Legal Portal / SharePoint / Email",
        event: "legal_doc.submitted",
        howItStarts:
          "A legal document is submitted for AI-powered enforceability analysis and risk assessment",
        setup:
          "Configure your legal intake portal to POST documents to this workflow URL. Include the full document content, jurisdiction, and analysis priority.",
        inputFields: [],
      }),

      // 2. High-fidelity text extraction
      aiNode(
        "2",
        "AI extract legal document text & metadata",
        280,
        "Read this legal document submission payload and perform high-fidelity text extraction regardless of format. Preserve paragraph numbering, section headers, defined terms, and cross-references. Identify the document type (contract/agreement/terms/policy/court_filing), jurisdiction, parties involved, and effective date. Respond JSON: {legal_text: string, document_type: string, jurisdiction: string, parties: string, effective_date: string, submitter_email: string, section_count: number, defined_terms: string}",
        "{webhook_payload}",
        [
          { field: "legal_text", as: "legal_text", type: "string" },
          { field: "document_type", as: "document_type", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "parties", as: "parties", type: "string" },
          { field: "effective_date", as: "effective_date", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "section_count", as: "section_count", type: "number" },
          { field: "defined_terms", as: "defined_terms", type: "string" },
        ],
      ),

      // 3. Load legal standards
      n("3", "hitApi", "Fetch legal standards & precedent database", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.your-company.com/legal/standards?type={document_type}&jurisdiction={jurisdiction}",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "standards",
              customName: "legal_standards",
              type: "string",
            },
            {
              name: "precedents",
              customName: "relevant_precedents",
              type: "string",
            },
            {
              name: "jurisdiction_rules",
              customName: "jurisdiction_rules",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI enforceability analysis
      aiNode(
        "4",
        "AI analyze enforceability & legal risks",
        760,
        "You are a senior legal analyst. Perform an enforceability analysis of this legal document. For each section: assess enforceability under the specified jurisdiction, identify ambiguous language, flag missing required provisions, check for unconscionable terms, verify proper consideration and capacity, and note any provisions that conflict with applicable law or precedent. Rate overall enforceability and risk. Respond JSON: {enforceability_analysis: string, overall_enforceability: string, risk_score: number, critical_issues: string, critical_issue_count: number, ambiguous_clauses: string, missing_provisions: string, conflicting_provisions: string}",
        "Document ({document_type}): {legal_text}, Jurisdiction: {jurisdiction}, Parties: {parties}, Standards: {legal_standards}, Precedents: {relevant_precedents}, Rules: {jurisdiction_rules}",
        [
          {
            field: "enforceability_analysis",
            as: "enforceability_analysis",
            type: "string",
          },
          {
            field: "overall_enforceability",
            as: "overall_enforceability",
            type: "string",
          },
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "critical_issues", as: "critical_issues", type: "string" },
          {
            field: "critical_issue_count",
            as: "critical_issue_count",
            type: "number",
          },
          {
            field: "ambiguous_clauses",
            as: "ambiguous_clauses",
            type: "string",
          },
          {
            field: "missing_provisions",
            as: "missing_provisions",
            type: "string",
          },
          {
            field: "conflicting_provisions",
            as: "conflicting_provisions",
            type: "string",
          },
        ],
      ),

      // 5. Critical risk condition
      n("5", "condition", "Has critical enforceability issues?", 1000, {
        conditionData: [
          {
            variable: "critical_issue_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Escalate to legal team
      slackNode(
        "6",
        "Escalate to legal: critical enforceability risk",
        1240,
        "LEGAL ALERT — Critical enforceability issues found\\nDocument: {document_type} | Jurisdiction: {jurisdiction}\\nParties: {parties}\\nRisk score: {risk_score}/100\\nCritical issues: {critical_issue_count}\\n\\n{critical_issues}\\n\\nMissing provisions:\\n{missing_provisions}\\n\\nConflicting provisions:\\n{conflicting_provisions}",
        460,
      ),

      // 7. Generate annotated review artifact
      aiNode(
        "7",
        "AI generate annotated review document",
        1480,
        "You are a legal editor. Generate an annotated review of this legal document. For each section with issues: quote the problematic text, explain the issue, cite the applicable standard or precedent, and provide recommended revised language. Format as a professional legal memorandum. Respond JSON: {annotated_review: string, total_annotations: number, memo_summary: string}",
        "Document: {legal_text}, Analysis: {enforceability_analysis}, Critical issues: {critical_issues}, Ambiguous: {ambiguous_clauses}, Missing: {missing_provisions}",
        [
          { field: "annotated_review", as: "annotated_review", type: "string" },
          {
            field: "total_annotations",
            as: "total_annotations",
            type: "number",
          },
          { field: "memo_summary", as: "memo_summary", type: "string" },
        ],
      ),

      // 8. Notify and create tracking issue
      emailNode(
        "8",
        "Email: legal analysis complete",
        1720,
        "{submitter_email}",
        "Legal Analysis: {document_type} — Risk {risk_score}/100 | {overall_enforceability}",
        "Legal Document Analysis Complete\\n\\nDocument: {document_type} | Jurisdiction: {jurisdiction}\\nParties: {parties} | Effective date: {effective_date}\\nSections analyzed: {section_count}\\n\\nEnforceability: {overall_enforceability}\\nRisk score: {risk_score}/100\\nCritical issues: {critical_issue_count}\\nTotal annotations: {total_annotations}\\n\\nMemo Summary:\\n{memo_summary}\\n\\nAmbiguous clauses:\\n{ambiguous_clauses}\\n\\nMissing provisions:\\n{missing_provisions}",
      ),

      // 9. Immutable evidence archive
      n("9", "hitApi", "Archive analysis to immutable evidence store", 1960, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{s3_bucket}.s3.amazonaws.com/legal-evidence/{document_type}-{timestamp}.json",
          headers: [
            { key: "Authorization", value: "{aws_sig_v4}" },
            { key: "Content-Type", value: "application/json" },
            { key: "x-amz-storage-class", value: "GLACIER_IR" },
          ],
          body: [
            { key: "document_type", value: "{document_type}" },
            { key: "jurisdiction", value: "{jurisdiction}" },
            { key: "parties", value: "{parties}" },
            { key: "risk_score", value: "{risk_score}" },
            { key: "enforceability", value: "{overall_enforceability}" },
            { key: "analysis", value: "{enforceability_analysis}" },
            { key: "annotated_review", value: "{annotated_review}" },
          ],
          variables: [
            { name: "ETag", customName: "evidence_etag", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> escalate
      e("5", "7", "output_2"), // non-critical -> annotated review
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Legal Document Analyzer** — Enforceability analysis with jurisdiction-aware risk scoring and immutable archival.\n\n1. Legal document submitted via webhook from legal portal\n2. **AI extracts** text with preserved structure, parties, and jurisdiction\n3. **Legal standards API** loads applicable standards and precedents\n4. **AI analyzes** enforceability, ambiguity, missing provisions, and conflicts\n5. Critical risk gate triggers immediate legal escalation\n6. **Slack alert** to legal team for critical enforceability issues\n7. **AI generates** annotated legal memorandum with revised language\n8. **Email notification** to submitter with full analysis\n9. Complete analysis **archived** to S3 Glacier for immutable evidence\n\n**To activate:** Say "connect openai", "connect your legal standards API", "connect slack", "connect aws s3"`,
  },

  // ===================================================================
  // 69. AI MEETING NOTES (8 nodes)
  // ===================================================================

  "AI Meeting Notes": {
    nodes: [
      trigger("Webhook: Meeting recording complete", "webhook_app", {
        source: "Zoom / Google Meet / Teams / Otter.ai",
        event: "meeting.ended",
        howItStarts:
          "A meeting recording and transcript become available after the meeting ends",
        setup:
          "Configure Zoom -> Marketplace -> Webhooks -> meeting.ended, or Otter.ai webhook to POST meeting data to this workflow URL when transcription completes.",
        inputFields: [],
      }),

      // 2. Transcription + speaker attribution
      aiNode(
        "2",
        "AI process transcript with speaker attribution",
        280,
        "Read this meeting data payload and extract the transcript with speaker attribution regardless of format. Clean up the transcript: fix speaker names, remove filler words, identify speaker changes, and segment by topic. Respond JSON: {transcript: string, meeting_title: string, meeting_date: string, duration_minutes: number, speakers: string, speaker_count: number, organizer_email: string, attendee_emails: string}",
        "{webhook_payload}",
        [
          { field: "transcript", as: "transcript", type: "string" },
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "speakers", as: "speakers", type: "string" },
          { field: "speaker_count", as: "speaker_count", type: "number" },
          { field: "organizer_email", as: "organizer_email", type: "string" },
          { field: "attendee_emails", as: "attendee_emails", type: "string" },
        ],
      ),

      // 3. AI extract decisions and action items
      aiNode(
        "3",
        "AI extract decisions, actions & key topics",
        520,
        "You are a professional meeting note-taker. From this transcript, extract: (1) Key decisions made — who decided what and the context, (2) Action items — task, owner, due date if mentioned, priority, (3) Discussion topics — main themes covered with key points, (4) Open questions — unresolved items needing follow-up, (5) Parking lot — topics deferred to future meetings. For each action item, rate assignee confidence 0-100 based on how clearly it was assigned. Respond JSON: {decisions: string, action_items: string, topics: string, open_questions: string, parking_lot: string, decision_count: number, action_count: number, low_confidence_actions: number}",
        "Meeting: {meeting_title}, Speakers: {speakers}, Transcript: {transcript}",
        [
          { field: "decisions", as: "decisions", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "topics", as: "topics", type: "string" },
          { field: "open_questions", as: "open_questions", type: "string" },
          { field: "parking_lot", as: "parking_lot", type: "string" },
          { field: "decision_count", as: "decision_count", type: "number" },
          { field: "action_count", as: "action_count", type: "number" },
          {
            field: "low_confidence_actions",
            as: "low_confidence_actions",
            type: "number",
          },
        ],
      ),

      // 4. AI executive summary
      aiNode(
        "4",
        "AI generate executive meeting summary",
        760,
        "You are an executive assistant. Write a concise executive summary of this meeting (under 250 words). Lead with the most important outcome, then cover decisions, key discussion points, and next steps. Make it scannable with bullet points. Respond JSON: {executive_summary: string, one_line_summary: string, next_meeting_topics: string}",
        "Meeting: {meeting_title}, Decisions: {decisions}, Actions: {action_items}, Topics: {topics}, Questions: {open_questions}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "one_line_summary", as: "one_line_summary", type: "string" },
          {
            field: "next_meeting_topics",
            as: "next_meeting_topics",
            type: "string",
          },
        ],
      ),

      // 5. Create tasks with assignee confidence check
      n("5", "hitApi", "Create action item tasks in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Meeting Action: {meeting_title}","notes":"From meeting: {meeting_title} ({meeting_date})\\n\\nAction items:\\n{action_items}\\n\\nDecisions:\\n{decisions}","projects":["{meeting_actions_project_id}"]}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "task_id", type: "string" },
          ],
        },
      }),

      // 6. Escalation condition for unclear actions
      n("6", "condition", "Has low-confidence action assignments?", 1240, {
        conditionData: [
          {
            variable: "low_confidence_actions",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Publish to workspace (Notion)
      n("7", "hitApi", "Publish meeting notes to Notion", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{meeting_notes_db_id}"}' },
            {
              key: "properties",
              value:
                '{"Meeting":{"title":[{"text":{"content":"{meeting_title}"}}]},"Date":{"date":{"start":"{meeting_date}"}},"Duration":{"number":{duration_minutes}},"Decisions":{"number":{decision_count}},"Actions":{"number":{action_count}}}',
            },
            {
              key: "children",
              value:
                '[{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Executive Summary"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{executive_summary}"}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Decisions"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{decisions}"}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"text":{"content":"Action Items"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"text":{"content":"{action_items}"}}]}}]',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),

      // 8. Email attendees
      emailNode(
        "8",
        "Email attendees: meeting notes published",
        1720,
        "{attendee_emails}",
        "Meeting Notes: {meeting_title} — {one_line_summary}",
        "Meeting Notes — {meeting_title}\\nDate: {meeting_date} | Duration: {duration_minutes} min | Speakers: {speaker_count}\\n\\n{executive_summary}\\n\\nDecisions ({decision_count}):\\n{decisions}\\n\\nAction Items ({action_count}):\\n{action_items}\\n\\nOpen Questions:\\n{open_questions}\\n\\nParking Lot:\\n{parking_lot}\\n\\nFull notes: https://notion.so/{notion_page_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // unclear actions -> publish with flag (organizer sees it)
      e("6", "7", "output_2"), // all clear -> publish normally
      e("7", "8"),
    ],
    summary: `**AI Meeting Notes** — Automatic transcription processing, decision extraction, and action item creation.\n\n1. Meeting recording webhook fires when meeting ends\n2. **AI processes** transcript with speaker attribution and topic segmentation\n3. **AI extracts** decisions, action items with owners, and open questions\n4. **AI writes** concise executive summary\n5. **Asana tasks** created from action items\n6. Confidence check flags unclear action assignments\n7. Full notes **published** to Notion workspace\n8. **Email sent** to all attendees with summary and Notion link\n\n**To activate:** Say "connect openai", "connect zoom/otter", "connect asana", "connect notion"`,
  },

  // ===================================================================
  // 70. AI MEETING SCHEDULER (8 nodes)
  // ===================================================================

  "AI Meeting Scheduler": {
    nodes: [
      trigger("Webhook: Meeting scheduling request", "webhook_app", {
        source: "Slack / Email / Internal Tool",
        event: "schedule.requested",
        howItStarts:
          "A team member requests a meeting with specific participants, agenda, and time preferences",
        setup:
          "Configure a Slack slash command (/schedule-meeting) or form to POST scheduling requests to this workflow URL. Include participants, agenda, duration, and urgency.",
        inputFields: [],
      }),

      // 2. Pull calendar availability
      aiNode(
        "2",
        "AI extract scheduling request details",
        280,
        "Read this scheduling request payload and extract all details regardless of format. Find the participant emails, meeting agenda/purpose, preferred duration, urgency, requester email, time zone preferences, and any constraints (e.g., before noon, not Fridays). Respond JSON: {participants: string, agenda: string, duration_minutes: number, urgency: string, requester_email: string, timezone: string, constraints: string, meeting_title: string}",
        "{webhook_payload}",
        [
          { field: "participants", as: "participants", type: "string" },
          { field: "agenda", as: "agenda", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "timezone", as: "timezone", type: "string" },
          { field: "constraints", as: "constraints", type: "string" },
          { field: "meeting_title", as: "meeting_title", type: "string" },
        ],
      ),

      // 3. Fetch free/busy for all participants
      n("3", "hitApi", "Pull free/busy from Google Calendar", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{tomorrow_iso}" },
            { key: "timeMax", value: "{next_week_iso}" },
            { key: "items", value: '[{"id":"{participants}"}]' },
          ],
          variables: [
            { name: "calendars", customName: "freebusy_data", type: "string" },
          ],
        },
      }),

      // 4. AI slot optimization
      aiNode(
        "4",
        "AI find optimal meeting slots",
        760,
        "You are a scheduling optimizer. Analyze the free/busy data for all participants and find the best meeting slots. Consider: all participants must be free, respect time zone preferences, honor constraints, prefer mornings for urgent meetings, avoid back-to-back scheduling, and minimize fragmentation of focus blocks. Rank the top 3 slots. Respond JSON: {slot_1: string, slot_2: string, slot_3: string, slots_found: number, all_available_for_slot_1: boolean, conflicts: string, recommendation: string}",
        "Participants: {participants}, Duration: {duration_minutes}min, Timezone: {timezone}, Constraints: {constraints}, Urgency: {urgency}, Free/busy: {freebusy_data}",
        [
          { field: "slot_1", as: "slot_1", type: "string" },
          { field: "slot_2", as: "slot_2", type: "string" },
          { field: "slot_3", as: "slot_3", type: "string" },
          { field: "slots_found", as: "slots_found", type: "number" },
          {
            field: "all_available_for_slot_1",
            as: "all_available_for_slot_1",
            type: "string",
          },
          { field: "conflicts", as: "conflicts", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),

      // 5. Single slot condition (can auto-book if everyone is free)
      n("5", "condition", "Single perfect slot found (all available)?", 1000, {
        conditionData: [
          {
            variable: "all_available_for_slot_1",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-book best slot
      n("6", "hitApi", "Auto-book meeting on Google Calendar", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_calendar_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "{meeting_title}" },
            {
              key: "description",
              value:
                "Agenda: {agenda}\\n\\nScheduled by AI Meeting Scheduler\\nRecommendation: {recommendation}",
            },
            {
              key: "start",
              value: '{"dateTime":"{slot_1}","timeZone":"{timezone}"}',
            },
            { key: "attendees", value: '[{"email":"{participants}"}]' },
            {
              key: "conferenceData",
              value: '{"createRequest":{"requestId":"ai-{timestamp}"}}',
            },
          ],
          variables: [
            { name: "id", customName: "event_id", type: "string" },
            { name: "htmlLink", customName: "event_link", type: "string" },
          ],
        },
      }),

      // 7. Create poll for multiple options
      slackNode(
        "7",
        "Post scheduling poll to Slack",
        1240,
        "Meeting scheduling needed: *{meeting_title}*\\nAgenda: {agenda}\\nDuration: {duration_minutes} min\\n\\nPlease vote for your preferred time:\\n:one: {slot_1}\\n:two: {slot_2}\\n:three: {slot_3}\\n\\nConflicts: {conflicts}\\nNote: {recommendation}",
        460,
      ),

      // 8. Confirmation send
      emailNode(
        "8",
        "Email: meeting confirmation or poll link",
        1480,
        "{requester_email}",
        "Meeting Scheduled: {meeting_title}",
        "Meeting Scheduling Update\\n\\nMeeting: {meeting_title}\\nAgenda: {agenda}\\nDuration: {duration_minutes} minutes\\n\\nSlots found: {slots_found}\\nRecommendation: {recommendation}\\n\\nSlot 1: {slot_1}\\nSlot 2: {slot_2}\\nSlot 3: {slot_3}\\n\\nConflicts: {conflicts}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // perfect slot -> auto-book
      e("5", "7", "output_2"), // no perfect slot -> poll
      e("6", "8"),
      e("7", "8"),
    ],
    summary: `**AI Meeting Scheduler** — Finds optimal slots across participants and auto-books or polls when needed.\n\n1. Scheduling request submitted via Slack command or webhook\n2. **AI extracts** participants, agenda, duration, and constraints\n3. **Google Calendar** free/busy check for all participants\n4. **AI optimizes** slot selection considering time zones, constraints, and focus blocks\n5. Condition checks if a single perfect slot exists\n6. Perfect slot: **auto-booked** on Google Calendar with video conference\n7. No perfect slot: **Slack poll** posted for team to vote\n8. **Email confirmation** sent to requester with meeting details\n\n**To activate:** Say "connect openai", "connect google calendar", "connect slack"`,
  },

  // ===================================================================
  // 71. AI PERSONA CHATBOT BUILDER (8 nodes)
  // ===================================================================

  "AI Persona Chatbot Builder": {
    nodes: [
      trigger("Webhook: Chatbot persona spec submitted", "webhook_app", {
        source: "Internal Tool / Admin Portal",
        event: "persona.submitted",
        howItStarts:
          "A chatbot persona specification is submitted with name, behavior rules, knowledge base, and target audience",
        setup:
          "Configure your admin portal to POST persona specifications to this workflow URL. Include persona name, personality traits, knowledge domain, tone, and any uploaded KB documents.",
        inputFields: [],
      }),

      // 2. Ingest KB and create embeddings
      aiNode(
        "2",
        "AI extract persona spec & prepare KB",
        280,
        "Read this persona chatbot specification payload and extract all details regardless of format. Identify the persona name, personality traits, knowledge domain, target audience, tone/style, response length preferences, topics to avoid, and KB documents to process. Respond JSON: {persona_name: string, personality: string, knowledge_domain: string, target_audience: string, tone: string, max_response_length: string, avoid_topics: string, kb_content: string, builder_email: string}",
        "{webhook_payload}",
        [
          { field: "persona_name", as: "persona_name", type: "string" },
          { field: "personality", as: "personality", type: "string" },
          { field: "knowledge_domain", as: "knowledge_domain", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "tone", as: "tone", type: "string" },
          {
            field: "max_response_length",
            as: "max_response_length",
            type: "string",
          },
          { field: "avoid_topics", as: "avoid_topics", type: "string" },
          { field: "kb_content", as: "kb_content", type: "string" },
          { field: "builder_email", as: "builder_email", type: "string" },
        ],
      ),

      // 3. Create embeddings for KB
      n("3", "hitApi", "Create KB embeddings via OpenAI", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{kb_content}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "kb_embedding",
              type: "string",
            },
            {
              name: "usage.total_tokens",
              customName: "embedding_tokens",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI create system prompt
      aiNode(
        "4",
        "AI craft optimized system prompt",
        760,
        "You are a prompt engineering expert. Create an optimized system prompt for this chatbot persona. The prompt should: define the persona clearly, set behavioral boundaries, embed the knowledge domain context, specify response formatting, include safety rails (no medical/legal/financial advice unless qualified), handle off-topic gracefully, and maintain consistent personality. Also generate 5 test conversation scenarios to validate. Respond JSON: {system_prompt: string, safety_rails: string, off_topic_handler: string, test_scenarios: string, prompt_token_count: number}",
        "Name: {persona_name}, Personality: {personality}, Domain: {knowledge_domain}, Audience: {target_audience}, Tone: {tone}, Max length: {max_response_length}, Avoid: {avoid_topics}",
        [
          { field: "system_prompt", as: "system_prompt", type: "string" },
          { field: "safety_rails", as: "safety_rails", type: "string" },
          {
            field: "off_topic_handler",
            as: "off_topic_handler",
            type: "string",
          },
          { field: "test_scenarios", as: "test_scenarios", type: "string" },
          {
            field: "prompt_token_count",
            as: "prompt_token_count",
            type: "number",
          },
        ],
      ),

      // 5. Automated test conversations
      aiNode(
        "5",
        "AI run automated test conversations",
        1000,
        "You are a QA tester. Run the 5 test scenarios against this system prompt. For each scenario: send the test message, evaluate the response for: persona consistency, factual accuracy (based on KB), tone adherence, safety rail compliance, and response length. Score each test 0-100 and compute overall quality. Respond JSON: {test_results: string, overall_quality_score: number, passed_tests: number, failed_tests: number, improvement_suggestions: string}",
        "System prompt: {system_prompt}, Test scenarios: {test_scenarios}, KB content: {kb_content}, Safety rails: {safety_rails}",
        [
          { field: "test_results", as: "test_results", type: "string" },
          {
            field: "overall_quality_score",
            as: "overall_quality_score",
            type: "number",
          },
          { field: "passed_tests", as: "passed_tests", type: "number" },
          { field: "failed_tests", as: "failed_tests", type: "number" },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "string",
          },
        ],
      ),

      // 6. Quality condition
      n("6", "condition", "Quality score >= 80 (4+ tests pass)?", 1240, {
        conditionData: [
          {
            variable: "overall_quality_score",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Deploy chatbot endpoint
      n("7", "hitApi", "Deploy chatbot endpoint via API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.your-company.com/chatbots/deploy",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{persona_name}" },
            { key: "system_prompt", value: "{system_prompt}" },
            { key: "kb_embedding_id", value: "{kb_embedding}" },
            { key: "safety_rails", value: "{safety_rails}" },
            { key: "off_topic_handler", value: "{off_topic_handler}" },
            { key: "max_response_length", value: "{max_response_length}" },
            { key: "quality_score", value: "{overall_quality_score}" },
          ],
          variables: [
            {
              name: "endpoint_url",
              customName: "chatbot_endpoint",
              type: "string",
            },
            { name: "chatbot_id", customName: "chatbot_id", type: "string" },
          ],
        },
      }),

      // 8. Notify builder
      emailNode(
        "8",
        "Email builder: chatbot deployed",
        1720,
        "{builder_email}",
        "Chatbot Deployed: {persona_name} — Quality {overall_quality_score}/100",
        "Chatbot Deployment Complete\\n\\nPersona: {persona_name}\\nDomain: {knowledge_domain}\\nAudience: {target_audience}\\n\\nQuality Score: {overall_quality_score}/100\\nTests passed: {passed_tests}/5\\n\\nEndpoint: {chatbot_endpoint}\\nChatbot ID: {chatbot_id}\\n\\nTest Results:\\n{test_results}\\n\\nImprovement suggestions:\\n{improvement_suggestions}\\n\\nThe chatbot is now live and ready for integration.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // quality pass -> deploy
      e("6", "4", "output_2"), // quality fail -> refine prompt
      e("7", "8"),
    ],
    summary: `**AI Persona Chatbot Builder** — Builds, tests, and deploys custom chatbot personas with quality gates.\n\n1. Persona specification submitted via webhook\n2. **AI extracts** persona traits, knowledge domain, and behavioral rules\n3. **OpenAI embeddings** created from knowledge base content\n4. **AI crafts** optimized system prompt with safety rails\n5. **AI runs** 5 automated test conversations and scores quality\n6. Quality gate ensures chatbot meets 80/100 threshold\n7. Passing chatbots **deployed** to production endpoint\n8. **Email notification** to builder with endpoint URL and test results\n\n**To activate:** Say "connect openai", "connect your deployment API", "connect email"`,
  },

  // ===================================================================
  // 72. AI POWERED FAQ GENERATOR (7 nodes)
  // ===================================================================

  "AI Powered FAQ Generator": {
    nodes: [
      trigger("Schedule: Weekly FAQ generation from tickets", "schedule", {
        schedule: "Every Friday at 10:00 AM",
        howItStarts:
          "Runs weekly to analyze support tickets and search logs, cluster common questions, and generate/update FAQ articles",
        setup:
          "Connect your support platform (Zendesk/Intercom) and help center. The workflow pulls recent tickets, identifies patterns, and auto-generates FAQ content.",
        inputFields: [],
      }),

      // 2. Pull tickets and search logs
      n("2", "hitApi", "Pull recent tickets from Zendesk", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket%20created>{week_ago}&sort_by=created_at&sort_order=desc&per_page=200",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth_b64}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "recent_tickets", type: "string" },
            { name: "count", customName: "total_tickets", type: "number" },
          ],
        },
      }),

      // 3. AI cluster questions
      aiNode(
        "3",
        "AI cluster common questions & identify FAQ topics",
        520,
        "You are a support analytics expert. Analyze these recent support tickets and cluster them by common question themes. Identify the top 10 most frequent question clusters. For each cluster provide: representative question, frequency count, sample ticket IDs, and current FAQ coverage (covered/partially/not_covered). Respond JSON: {question_clusters: string, cluster_count: number, uncovered_count: number, top_cluster_question: string, top_cluster_frequency: number, total_analyzed: number}",
        "Recent tickets: {recent_tickets}, Total: {total_tickets}",
        [
          {
            field: "question_clusters",
            as: "question_clusters",
            type: "string",
          },
          { field: "cluster_count", as: "cluster_count", type: "number" },
          { field: "uncovered_count", as: "uncovered_count", type: "number" },
          {
            field: "top_cluster_question",
            as: "top_cluster_question",
            type: "string",
          },
          {
            field: "top_cluster_frequency",
            as: "top_cluster_frequency",
            type: "number",
          },
          { field: "total_analyzed", as: "total_analyzed", type: "number" },
        ],
      ),

      // 4. AI draft grounded answers
      aiNode(
        "4",
        "AI draft FAQ answers grounded in resolved tickets",
        760,
        "You are a technical writer for a help center. For each uncovered or partially covered question cluster, draft a comprehensive FAQ answer. Ground each answer in the actual resolution from resolved tickets — do not fabricate solutions. Include: clear question title, step-by-step answer, troubleshooting tips, and related articles. Rate your confidence 0-100 for each answer. Respond JSON: {faq_drafts: string, draft_count: number, avg_confidence: number, high_confidence_count: number, medium_confidence_count: number, low_confidence_count: number}",
        "Question clusters: {question_clusters}, Uncovered: {uncovered_count}, Tickets: {recent_tickets}",
        [
          { field: "faq_drafts", as: "faq_drafts", type: "string" },
          { field: "draft_count", as: "draft_count", type: "number" },
          { field: "avg_confidence", as: "avg_confidence", type: "number" },
          {
            field: "high_confidence_count",
            as: "high_confidence_count",
            type: "number",
          },
          {
            field: "medium_confidence_count",
            as: "medium_confidence_count",
            type: "number",
          },
          {
            field: "low_confidence_count",
            as: "low_confidence_count",
            type: "number",
          },
        ],
      ),

      // 5. Confidence condition
      n("5", "condition", "Has high-confidence FAQs to auto-publish?", 1000, {
        conditionData: [
          {
            variable: "high_confidence_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Auto-publish high confidence to help center + SME review medium
      n(
        "6",
        "hitApi",
        "Publish high-confidence FAQs to Zendesk Help Center",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/help_center/sections/{faq_section_id}/articles.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth_b64}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "article",
                value:
                  '{"title":"{top_cluster_question}","body":"{faq_drafts}","draft":false,"promoted":true}',
              },
            ],
            variables: [
              {
                name: "article.id",
                customName: "published_article_id",
                type: "string",
              },
              {
                name: "article.html_url",
                customName: "article_url",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Notify support team
      slackNode(
        "7",
        "Notify support: FAQ updates published",
        1480,
        "Weekly FAQ Update\\n{draft_count} new FAQ drafts generated from {total_analyzed} tickets\\n\\nAuto-published: {high_confidence_count} (high confidence)\\nNeeds SME review: {medium_confidence_count} (medium confidence)\\nNeeds investigation: {low_confidence_count} (low confidence)\\n\\nTop question: *{top_cluster_question}* (asked {top_cluster_frequency} times)\\nAvg confidence: {avg_confidence}%\\n\\nPublished: {article_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // has high-confidence -> auto-publish
      e("5", "7", "output_2"), // only low-confidence -> skip publish, notify for review
      e("6", "7"),
    ],
    summary: `**AI Powered FAQ Generator** — Automatically generates FAQ articles from support ticket patterns with confidence-based publishing.\n\n1. Runs every Friday on schedule\n2. **Zendesk API** pulls the past week's support tickets\n3. **AI clusters** common questions and identifies uncovered FAQ topics\n4. **AI drafts** grounded FAQ answers from actual ticket resolutions\n5. Confidence gate routes high vs. low confidence drafts\n6. High-confidence: **auto-published** to Zendesk Help Center\n7. **Slack notification** to support team with publish summary and review queue\n\n**To activate:** Say "connect openai", "connect zendesk", "connect slack"`,
  },
};
